import React, { useCallback, useEffect, useState } from "react";
import { Button, Dropdown, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  Check, X, Package, Upload, Scroll, Trash2, BarChart3, FileText,
  Hammer, AlertTriangle, Download, RotateCw, Pencil, FolderOpen,
  Settings, Link as LinkIcon, ClipboardPaste, File as FileIcon, History,
  Play, Square, Anchor, Database, SquareTerminal, Wand2 } from "lucide-react";
import {
  createDeployApp,
  deleteDeployApp,
  deployBuildPush,
  deployHelm,
  deployHtmlContainer,
  deployPm2,
  deployRunGit,
  detectHtmlPort,
  dockerImageSaveUrl,
  fetchDeployApps,
  listHtmlVersions,
  revertHtmlContainer,
  helmHistory,
  helmRestartService,
  helmRollback,
  helmServices,
  helmStatus,
  inspectServerPath,
  listGitBranches,
  listNodeVersions,
  listStaticVersions,
  pm2Action,
  pm2Logs,
  pm2Status,
  readDeployFile,
  rollbackStaticRelease,
  stopDeployApp,
  successToast,
  switchGitBranch,
  updateDeployApp,
  uploadStaticBuild,
  writeDeployFile,
} from "../../redux/actionCreator";
import DeployWizard from "./DeployWizard";
import ComposeStatusModal from "./ComposeStatusModal";
import MongoPanel from "../Mongo/MongoPanel";
import "./applications.scss";

/**
 * Multi-application manager for a project. Each app (backend API, frontend,
 * worker…) is its own deploy unit with its own git/image, port, and target
 * servers. Cards show the essentials; the editor modal holds the full config.
 * "Deploy" runs a git-build for that app; "Wizard" runs the compose/k8s flow.
 */
const VALID_STAGES = new Set(["development", "staging", "production"]);
const ENV_OPTIONS = [
  { value: "development", label: "Development", color: "#2563eb", bg: "#dbeafe", tone: "info" },
  { value: "staging",     label: "Staging",     color: "#b45309", bg: "#fef3c7", tone: "warning" },
  { value: "production",  label: "Production",  color: "#b91c1c", bg: "#fee2e2", tone: "danger" },
];
const envMeta = (v) => ENV_OPTIONS.find((e) => e.value === v) || ENV_OPTIONS[0];
const envKey = (projectId) => `sht-deploy-env-${projectId}`;

export default function ApplicationsManager({
  projectId,
  onActivity,
  // The environment is owned by DeployPage and shared across tabs
  // (Applications / Cluster / Activity). We fall back to the localStorage
  // sticky value when rendered standalone (e.g. tests) so existing call
  // sites keep working.
  environment: environmentProp,
  onEnvironmentChange,
  // Lets DeployPage filter Cluster + Activity using the same apps list
  // we already fetched here. Called on every successful reload so the
  // parent's computed envServerIds stays in sync after deploy/edit/delete.
  onAppsChange,
}) {
  const dispatch = useDispatch();
  const [environmentInner, setEnvironmentInner] = useState(() => {
    try { return localStorage.getItem(envKey(projectId)) || "development"; }
    catch { return "development"; }
  });
  const environment = environmentProp || environmentInner;
  const setEnvironment = onEnvironmentChange || setEnvironmentInner;
  useEffect(() => { try { localStorage.setItem(envKey(projectId), environment); } catch {} }, [environment, projectId]);
  const [apps, setApps] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // app being edited
  const [wizardApp, setWizardApp] = useState(null);
  const [composeApp, setComposeApp] = useState(null);
  const [deployingId, setDeployingId] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);
  const [result, setResult] = useState(null); // { ok, name, results }
  const [uploadFor, setUploadFor] = useState(null); // app being uploaded to
  const [versionsFor, setVersionsFor] = useState(null); // app whose versions to show
  const [htmlUploadFor, setHtmlUploadFor] = useState(null); // html_container app being deployed
  const [htmlVersionsFor, setHtmlVersionsFor] = useState(null); // html_container app whose containers to show
  const [htmlBusyId, setHtmlBusyId] = useState(null);
  const [pm2BusyId, setPm2BusyId] = useState(null);
  const [pm2StatusFor, setPm2StatusFor] = useState(null); // app
  const [pm2LogsFor, setPm2LogsFor] = useState(null); // app
  const [branchPickerFor, setBranchPickerFor] = useState(null); // app
  const [buildPushBusyId, setBuildPushBusyId] = useState(null);
  const [helmBusyId, setHelmBusyId] = useState(null);
  const [helmStatusFor, setHelmStatusFor] = useState(null);
  const [helmHistoryFor, setHelmHistoryFor] = useState(null);
  // Mongo panel open-state — scoped per-app so it can reuse the app's
  // env_vars for URI resolution (MONGODB_URI, MONGO_URL, DATABASE_URL).
  // The MongoPanel itself handles the URI fallback to the server-level
  // mongo_uri when the app has no DB-related env var.
  const [mongoFor, setMongoFor] = useState(null);
  // Per-app single-service picker state. Multi-service charts (one helm
  // release that fans out into 7 microservice deployments) shouldn't
  // require a full chart rebuild every time one service changes. These
  // maps hold the currently-selected service name for each app card.
  const [pickedBuildSvc, setPickedBuildSvc] = useState({});   // appId → context name
  const [pickedRestartSvc, setPickedRestartSvc] = useState({}); // appId → deployment name
  const [restartSvcBusyId, setRestartSvcBusyId] = useState(null);
  // appId → { loading, services: string[], values_file, error }. Filled
  // lazily when a helm_chart app card first renders, and cleared when
  // the stage changes (services list is values-file-specific).
  const [helmSvcCache, setHelmSvcCache] = useState({});
  const [helmSvcBusyId, setHelmSvcBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const res = await dispatch(fetchDeployApps(projectId));
    setLoading(false);
    if (res?.status) {
      const list = res.data?.apps || [];
      setApps(list);
      setServers(res.data?.servers || []);
      onAppsChange && onAppsChange(list);
    }
  }, [dispatch, projectId, onAppsChange]);

  useEffect(() => { load(); }, [load]);

  const addApp = async () => {
    const name = window.prompt(
      `Application name (e.g. Backend API, Frontend) — will be created in ${environment.toUpperCase()}:`,
      "",
    );
    if (!name?.trim()) return;
    // New apps inherit the current env switcher so each stage = a separate
    // app. To stand up the same code in another stage, switch the env tab
    // and add a sibling with the same name there.
    const res = await dispatch(createDeployApp(projectId, name.trim(), environment));
    if (res?.status) { await load(); setEditing(res.data); }
    else alert(res?.message || "Couldn't create application");
  };

  // Filter apps to the active env's stage. Apps with no `stage` field
  // (legacy data, pre-feature) default to "development" so they don't
  // silently disappear from the list.
  const stageFor = (a) => (a?.stage && VALID_STAGES.has(a.stage)) ? a.stage : "development";
  const filteredApps = apps.filter((a) => stageFor(a) === environment);

  const removeApp = async (app) => {
    if (!window.confirm(`Delete application "${app.name}"? (Does not remove running containers.)`)) return;
    const res = await dispatch(deleteDeployApp(projectId, app._id));
    if (res?.status) load();
    else alert(res?.message || "Delete failed");
  };

  // Git-build deploy for one app (clone/pull → docker build → run with its port).
  const gitDeploy = async (app) => {
    if (!app.git_url || !app.repo_dir || !app.container_name) {
      alert("This app needs git_url, repo_dir and container_name set. Click Edit first.");
      return;
    }
    const targets = (app.target_server_ids || []);
    if (!targets.length) { alert("Pick at least one target server in Edit."); return; }
    if (!window.confirm(`Deploy "${app.name}" (git build) to ${targets.length} server(s)?`)) return;
    setDeployingId(app._id);
    setResult(null);
    const res = await dispatch(deployRunGit(projectId, { app_id: app._id }));
    setDeployingId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // PM2 — runs on the chosen target servers via SSH; no Docker involved.
  const pm2Deploy = async (app) => {
    if (!app.git_url || !app.repo_dir) {
      alert("This app needs git_url and repo_dir set. Click Edit first.");
      return;
    }
    if (!(app.target_server_ids || []).length) {
      alert("Pick at least one target server in Edit.");
      return;
    }
    if (!window.confirm(`Deploy "${app.name}" to ${environment.toUpperCase()} via PM2?\n(git pull → npm install → pm2 reload/start)`)) return;
    if (environment === "production" && !window.confirm(`Final check: this will hit PRODUCTION. Continue?`)) return;
    setPm2BusyId(app._id);
    setResult(null);
    const res = await dispatch(deployPm2(projectId, app._id, environment));
    setPm2BusyId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // action: "restart" | "reload" | "stop" | "delete"
  const pm2Run = async (app, action) => {
    const confirmText = {
      restart: `Restart "${app.name}" via PM2?`,
      reload: `Zero-downtime reload "${app.name}" via PM2?`,
      stop: `Stop "${app.name}" via PM2? (process stays registered)`,
      delete: `Delete "${app.name}" from PM2? (removes the process entirely)`,
    }[action];
    if (!window.confirm(confirmText)) return;
    setPm2BusyId(app._id);
    setResult(null);
    const res = await dispatch(pm2Action(projectId, app._id, action, environment));
    setPm2BusyId(null);
    setResult({ ok: !!res?.status, name: `${app.name} — pm2 ${action}`, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  const buildPushDeploy = async (app) => {
    if (!app.registry_url) { alert("registry_url required (edit the app first)"); return; }
    if (!app.git_url || !app.repo_dir) { alert("git_url + repo_dir required"); return; }
    const ctxCount = (app.build_contexts || []).length || 1;
    if (!window.confirm(
      `Build & push ${ctxCount} image${ctxCount === 1 ? "" : "s"} for "${app.name}" to ${app.registry_url}?\n\n` +
      `Stage: ${environment.toUpperCase()}`,
    )) return;
    setBuildPushBusyId(app._id);
    setResult(null);
    const res = await dispatch(deployBuildPush(projectId, app._id, environment));
    setBuildPushBusyId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  const helmDeploy = async (app) => {
    if (!app.git_url || !app.repo_dir) { alert("git_url + repo_dir required"); return; }
    if (!app.helm_chart_dir) { alert("helm_chart_dir required (e.g. \"helm-chart\")"); return; }
    if (!window.confirm(`helm upgrade --install for "${app.name}" on ${environment.toUpperCase()}?`)) return;
    if (environment === "production" && !window.confirm(`Final check: this hits PRODUCTION. Continue?`)) return;
    setHelmBusyId(app._id);
    setResult(null);
    const res = await dispatch(deployHelm(projectId, app._id, environment));
    setHelmBusyId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // Trigger a streaming download of `docker save <ref>` from one of an
  // app's target servers. Uses the same anchor-click pattern as the
  // Docker page — the browser streams the response straight to disk
  // and the `urltoken` cookie travels with the request automatically.
  // No axios fetch (would buffer a multi-GB tar in memory).
  const downloadImageRef = (ref, serverId, { gzip = false } = {}) => {
    if (!ref) return;
    if (!serverId) {
      // The app needs at least one target server for the download. The
      // Build & Push flow already requires this, so we shouldn't hit
      // this branch in normal use — but guard anyway.
      alert("No server available — set Target servers on this app first.");
      return;
    }
    if (!window.confirm(`Download "${ref}" as ${gzip ? ".tar.gz" : ".tar"}?\n\nLarge images may take a while.`)) return;
    const url = dockerImageSaveUrl(projectId, serverId, ref, { gzip });
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.setAttribute("download", "");
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ── Per-app terminal ──────────────────────────────────────────
  //
  // Which directory does this app "live in" on the target server?
  //   static → the `current` symlink, i.e. the release actually being
  //            served, which is what you want to inspect
  //   everything else → repo_dir (git_build / pm2_node /
  //            docker_build_push / helm_chart all clone into it)
  //   tar_upload → nothing sensible; returns "" so the button hides
  const deployDirFor = (app) => {
    if (!app) return "";
    if (app.mode === "static") {
      const root = (app.static_root || "").trim().replace(/\/+$/, "");
      return root ? `${root}/current` : "";
    }
    return (app.repo_dir || "").trim();
  };

  // Resolve an app's target server ids into the full server objects we
  // already hold in `servers` (loaded via fetchProjectServers), dropping
  // any id that no longer matches a server on the project.
  const serversForApp = (app) =>
    (app.target_server_ids || [])
      .map((id) => servers.find((s) => String(s._id) === String(id)))
      .filter(Boolean);

  // Open the browser terminal already sitting in the app's directory.
  // Same shape as the openTerminal helpers on the Projects, Docker and
  // Cluster pages — the backend WS validates `cwd` and cd's once the
  // shell is live.
  const openAppTerminal = (app, server) => {
    if (!server) return;
    const dir = deployDirFor(app);
    const qs = new URLSearchParams({
      label: `${app.name} · ${server.label}`,
      host: `${server.username}@${server.host}:${server.port}`,
    });
    if (dir) qs.set("cwd", dir);
    window.open(
      `/terminal/${projectId}/${server._id}?${qs.toString()}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  // ── HTML container ────────────────────────────────────────────
  //
  // A git-sourced deploy has nothing to pick, so it fires straight off;
  // upload-sourced deploys go through HtmlDeployModal for the file.
  const htmlGitDeploy = async (app) => {
    if (!app.git_url) { alert("Set git_url on this app first (click Edit)."); return; }
    if (!window.confirm(
      `Deploy "${app.name}" from ${app.git_url}#${app.git_branch || "main"}?\n\n` +
      `A new container is created and the current one is stopped ` +
      `(kept for revert, along with the ${Math.max(1, (app.keep_versions || 5) - 1)} before it).`
    )) return;
    setHtmlBusyId(app._id);
    setResult(null);
    const res = await dispatch(deployHtmlContainer(projectId, app._id, { source: "git" }));
    setHtmlBusyId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // The portal deliberately does NOT write nginx config for these apps — this
  // is just the block to paste into the Subdomains page, filled in with the
  // app's real domain and port so nothing has to be looked up.
  const nginxSnippetFor = (app) => [
    `server {`,
    `    listen 80;`,
    `    server_name ${app.domain || "<your-domain>"};`,
    ``,
    `    location / {`,
    `        proxy_pass http://127.0.0.1:${app.port || "<port>"};`,
    `        proxy_set_header Host              $host;`,
    `        proxy_set_header X-Real-IP         $remote_addr;`,
    `        proxy_set_header X-Forwarded-For   $proxy_add_x_forwarded_for;`,
    `        proxy_set_header X-Forwarded-Proto $scheme;`,
    `    }`,
    `}`,
  ].join("\n");

  // Build & push just ONE of the configured build contexts. Cuts a 7-service
  // chart's rebuild time down to a single image when only one changed.
  const buildPushOne = async (app) => {
    const svc = pickedBuildSvc[app._id] || (app.build_contexts || [])[0]?.name;
    if (!svc) { alert("This app has no build contexts to pick from."); return; }
    if (!app.registry_url) { alert("registry_url required (edit the app first)"); return; }
    if (!window.confirm(`Build & push only "${svc}" for "${app.name}" [${environment.toUpperCase()}]?`)) return;
    setBuildPushBusyId(app._id);
    setResult(null);
    const res = await dispatch(deployBuildPush(projectId, app._id, environment, [svc]));
    setBuildPushBusyId(null);
    setResult({ ok: !!res?.status, name: `${app.name} — ${svc}`, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // Restart ONE deployment in the helm-managed namespace via
  // `kubectl rollout restart`. No `helm upgrade` — other services stay
  // untouched. Pair with buildPushOne for a one-service redeploy.
  const helmRestartOne = async (app) => {
    const svc = (pickedRestartSvc[app._id] || "").trim();
    if (!svc) { alert("Pick the service to restart."); return; }
    if (!window.confirm(`kubectl rollout restart deployment/${svc} on ${environment.toUpperCase()}?`)) return;
    setRestartSvcBusyId(app._id);
    setResult(null);
    const res = await dispatch(helmRestartService(projectId, app._id, svc, environment));
    setRestartSvcBusyId(null);
    setResult({ ok: !!res?.status, name: `${app.name} — restart ${svc}`, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  // Fetch the service list for a helm_chart app from its values file.
  // The backend SSHes to the first target server, reads the resolved
  // values file (same precedence as deployHelm), and returns the
  // `services:` keys. Cached per appId to avoid re-fetching on every
  // render; cleared when the environment changes.
  const loadHelmServices = useCallback(async (app, opts = {}) => {
    const force = !!opts.force;
    const cached = helmSvcCache[app._id];
    if (cached && !force && !cached.error) return cached;
    setHelmSvcBusyId(app._id);
    const res = await dispatch(helmServices(projectId, app._id, environment));
    setHelmSvcBusyId(null);
    const entry = {
      loading: false,
      services: res?.data?.services || [],
      values_file: res?.data?.values_file || null,
      note: res?.data?.note,
      error: res?.status ? null : (res?.message || "Failed to read values file"),
    };
    setHelmSvcCache((m) => ({ ...m, [app._id]: entry }));
    // Pre-select the first service so the dropdown isn't empty.
    if (entry.services.length && !pickedRestartSvc[app._id]) {
      setPickedRestartSvc((m) => ({ ...m, [app._id]: entry.services[0] }));
    }
    return entry;
  }, [dispatch, projectId, environment, helmSvcCache, pickedRestartSvc]);

  // Stage switch → flush the service cache + dropdown picks. A
  // values-development.yaml may declare a different services list than
  // values-staging.yaml; keeping the old picks would be misleading.
  useEffect(() => {
    setHelmSvcCache({});
    setPickedRestartSvc({});
  }, [environment]);

  const stopApp = async (app, remove) => {
    if (!app.container_name) { alert("This app has no container_name set."); return; }
    const verb = remove ? "remove the container for" : "stop";
    if (!window.confirm(`${remove ? "Remove" : "Stop"} ${app.name}? This will ${verb} "${app.container_name}" on its target server(s).`)) return;
    setStoppingId(app._id);
    setResult(null);
    const res = await dispatch(stopDeployApp(projectId, app._id, remove));
    setStoppingId(null);
    setResult({ ok: !!res?.status, name: app.name, results: res?.data?.results || [], message: res?.message });
    load();
    onActivity && onActivity();
  };

  return (
    <div className="apps-manager">
      <div className="apps-head">
        <div>
          <h5 className="m-0">Applications</h5>
          <small className="text-muted">Multiple deployable apps per project — each with its own image, port &amp; servers.</small>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {/* The env switcher lives on the page header (above Guided
              deploy) since it controls every sub-tab. */}
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻"}
          </Button>
          <Button size="sm" variant="primary" onClick={addApp}>+ Add application</Button>
        </div>
      </div>

      {result && (
        <div className={`alert ${result.ok ? "alert-success" : "alert-danger"} mt-2`}>
          <div className="d-flex justify-content-between">
            <strong className="d-inline-flex align-items-center gap-2">
              {result.ok ? <Check size={14} /> : <X size={14} />} {result.name}
            </strong>
            <button type="button" className="btn-close" onClick={() => setResult(null)} />
          </div>
          {result.message && <div className="small">{result.message}</div>}
          {/* Post-build image downloads. The Build & Push controller
              returns the pushed image refs per server; surface them
              here as one-click download links so the operator can
              grab a fresh tar (e.g. for an air-gapped server) right
              after the build, while the image is still on the
              builder's local docker daemon. */}
          {result.ok && result.results.some((r) => (r.images || []).length > 0) && (
            <div className="mt-2 d-flex flex-wrap gap-2">
              {result.results.flatMap((r) =>
                (r.images || []).map((img) => (
                  <Dropdown key={`${r.server_id}-${img.ref}`} as="span">
                    <Dropdown.Toggle as={Button} size="sm" variant="outline-primary" className="d-inline-flex align-items-center gap-1">
                      <Download size={12} /> {img.name}:{img.tag}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => downloadImageRef(img.ref, r.server_id)}>
                        <Download size={12} className="me-2" /> Download .tar
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => downloadImageRef(img.ref, r.server_id, { gzip: true })}>
                        <Download size={12} className="me-2" /> Download .tar.gz
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ))
              )}
            </div>
          )}
          {result.results.map((r) => (
            <pre key={r.server_id} className="apps-log">{`# ${r.server_label || r.server_id} — ${r.status}\n${r.log || r.message || ""}`}</pre>
          ))}
        </div>
      )}

      {apps.length === 0 ? (
        <div className="apps-empty">
          <div className="empty-art"><Package size={32} strokeWidth={1.2} /></div>
          <p>No applications yet. Add your backend API, frontend, worker, etc. — each deploys independently.</p>
        </div>
      ) : filteredApps.length === 0 ? (
        <div className="apps-empty">
          <div className="empty-art"><Package size={32} strokeWidth={1.2} /></div>
          <p>
            No apps in <strong>{environment}</strong> yet. Switch tabs to see {apps.length - filteredApps.length}
            {" "}app{apps.length - filteredApps.length === 1 ? "" : "s"} on other stages, or add one here.
          </p>
        </div>
      ) : (
        <div className="apps-grid">
          {filteredApps.map((app) => (
            <div key={app._id} className="app-card">
              <div className="app-card-head">
                <strong className="app-name">
                  {app.name}
                  {(() => {
                    const m = envMeta(stageFor(app));
                    return (
                      <span
                        className="badge ms-2"
                        style={{ background: m.bg, color: m.color, fontWeight: 600, fontSize: 10 }}
                      >
                        {m.label}
                      </span>
                    );
                  })()}
                </strong>
                <span className="app-port">{app.port ? `:${app.port}` : "no port"}</span>
              </div>
              <div className="app-card-body">
                <div className="app-row">
                  <span>{app.mode === "pm2_node" ? "PM2 name" : "Container"}</span>
                  <code>{app.container_name || "—"}</code>
                </div>
                <div className="app-row">
                  <span>Source</span>
                  <code className="small">
                    {app.mode === "pm2_node"
                      ? `pm2 · ${app.node_script || "app.js"}${app.pm2_instances > 1 ? ` ×${app.pm2_instances}` : ""}`
                      : app.git_url
                        ? "git"
                        : app.image_ref || "—"}
                  </code>
                </div>
                {app.mode === "pm2_node" && (
                  <div className="app-row">
                    <span>Node</span>
                    <code className="small">{app.node_version_spec || "default (system PATH)"}</code>
                  </div>
                )}
                {app.git_url && (
                  <div className="app-row">
                    <span>Branch</span>
                    <button
                      type="button"
                      className="app-branch-chip"
                      title="Click to switch branch"
                      onClick={() => setBranchPickerFor(app)}
                    >
                       {app.git_branch || "main"} ▾
                    </button>
                  </div>
                )}
                {app.mode === "html_container" && (
                  <>
                    <div className="app-row">
                      <span>Domain</span>
                      {app.domain ? (
                        <a href={`http://${app.domain}`} target="_blank" rel="noopener noreferrer" className="small">
                          {app.domain}
                        </a>
                      ) : <code className="small">not set</code>}
                    </div>
                    <div className="app-row">
                      <span>Port</span>
                      <code className="small">
                        {app.port || "—"}
                        {app.port_source !== "manual" && <span className="text-muted"> (auto)</span>}
                      </code>
                    </div>
                    <div className="app-row"><span>Keep</span><span>{app.keep_versions || 5} versions</span></div>
                  </>
                )}
                <div className="app-row"><span>Servers</span><span>{(app.target_server_ids || []).length || 0}</span></div>
              </div>
              <div className="app-card-actions">
                {app.mode === "static" ? (
                  <>
                    <Button size="sm" variant="success" onClick={() => setUploadFor(app)} title="Upload a build (.zip / .tar.gz)">
                      <Upload size={14} className="me-1" /> Upload build
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setVersionsFor(app)} title="Browse and roll back to previous releases">
                      <Scroll size={14} className="me-1" /> Versions
                    </Button>
                  </>
                ) : app.mode === "html_container" ? (
                  <>
                    <Button
                      size="sm" variant="success" disabled={htmlBusyId === app._id}
                      onClick={() => setHtmlUploadFor(app)}
                      title="Upload a .zip / .tar.gz of the site and run it in a new container"
                    >
                      <Upload size={14} className="me-1" /> Deploy upload
                    </Button>
                    {app.git_url && (
                      <Button
                        size="sm" variant="outline-success" disabled={htmlBusyId === app._id}
                        onClick={() => htmlGitDeploy(app)}
                        title={`Clone ${app.git_url}#${app.git_branch || "main"} on the server and run it`}
                      >
                        {htmlBusyId === app._id
                          ? <Spinner size="sm" animation="border" />
                          : <><Play size={14} className="me-1" /> Deploy git</>}
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline-primary"
                      onClick={() => setHtmlVersionsFor(app)}
                      title="The retained containers — revert to any of them"
                    >
                      <Scroll size={14} className="me-1" /> Versions
                    </Button>
                    <Button
                      size="sm" variant="outline-secondary"
                      onClick={() => {
                        const snippet = nginxSnippetFor(app);
                        navigator.clipboard?.writeText(snippet).then(
                          () => successToast(`nginx block for ${app.domain || "this app"} copied`),
                          () => window.prompt("Copy this into your nginx config:", snippet),
                        );
                      }}
                      title="Copy an nginx proxy_pass block for this app's domain and port"
                    >
                      <ClipboardPaste size={14} className="me-1" /> nginx
                    </Button>
                  </>
                ) : app.mode === "pm2_node" ? (
                  <>
                    <Button size="sm" variant="success" disabled={pm2BusyId === app._id} onClick={() => pm2Deploy(app)} title="git pull → npm install → pm2 reload/start">
                      {pm2BusyId === app._id ? <Spinner size="sm" animation="border" /> : <><Play size={14} className="me-1" /> Deploy</>}
                    </Button>
                    <Button size="sm" variant="outline-warning" disabled={pm2BusyId === app._id} onClick={() => pm2Run(app, "restart")} title="pm2 restart"><RotateCw size={14} className="me-1" /> Restart</Button>
                    <Button size="sm" variant="outline-danger" disabled={pm2BusyId === app._id} onClick={() => pm2Run(app, "stop")} title="pm2 stop"><Square size={14} fill="currentColor" className="me-1" /> Stop</Button>
                    <Button size="sm" variant="outline-danger" disabled={pm2BusyId === app._id} onClick={() => pm2Run(app, "delete")} title="pm2 delete — removes the process from PM2"><Trash2 size={14} /></Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setPm2StatusFor(app)} title="pm2 jlist parsed"><BarChart3 size={14} className="me-1" /> Status</Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setPm2LogsFor(app)} title="pm2 logs --nostream"><FileText size={14} className="me-1" /> Logs</Button>
                  </>
                ) : app.mode === "docker_build_push" ? (
                  <>
                    <Button
                      size="sm" variant="success"
                      disabled={buildPushBusyId === app._id}
                      onClick={() => buildPushDeploy(app)}
                      title={`Build each context and push to ${app.registry_url || "<registry>"}`}
                    >
                      {buildPushBusyId === app._id ? <Spinner size="sm" animation="border" /> : <><Hammer size={14} className="me-1" /> Build & Push</>}
                    </Button>
                    {/* Single-context build. Only useful when the app has
                        more than one build_context — for a single-context
                        app "Build & Push" already does the same thing. */}
                    {(app.build_contexts || []).length > 1 && (
                      <div className="d-flex gap-1 align-items-center">
                        <Form.Select
                          size="sm"
                          style={{ width: 160 }}
                          value={pickedBuildSvc[app._id] || (app.build_contexts || [])[0]?.name || ""}
                          onChange={(e) => setPickedBuildSvc((m) => ({ ...m, [app._id]: e.target.value }))}
                          title="Pick which build context to (re)build"
                        >
                          {(app.build_contexts || []).map((c) => (
                            <option key={c.name} value={c.name}>{c.name}</option>
                          ))}
                        </Form.Select>
                        <Button
                          size="sm" variant="outline-success"
                          disabled={buildPushBusyId === app._id}
                          onClick={() => buildPushOne(app)}
                          title="Build & push only the selected context"
                        >
                          <Hammer size={14} className="me-1" /> one
                        </Button>
                      </div>
                    )}
                    {/* Standalone "Save image" dropdown — independent of
                        the build flow. Useful for grabbing a tar of an
                        image that was built previously without redoing
                        the whole build. Lists every build context with
                        the app's image_tag_strategy applied (defaults to
                        :latest). Pulls from the FIRST target server —
                        whichever built it last. */}
                    {(app.build_contexts || []).length > 0 && app.registry_url && (app.target_server_ids || []).length > 0 && (
                      <BuildPushSaveButton
                        app={app}
                        onPick={(ref, gzip) => downloadImageRef(ref, (app.target_server_ids || [])[0], { gzip })}
                      />
                    )}
                  </>
                ) : app.mode === "helm_chart" ? (
                  <>
                    <Button
                      size="sm" variant="success"
                      disabled={helmBusyId === app._id}
                      onClick={() => helmDeploy(app)}
                      title="helm upgrade --install with the active stage's values file"
                    >
                      {helmBusyId === app._id ? <Spinner size="sm" animation="border" /> : <><Anchor size={14} className="me-1" /> Deploy</>}
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setHelmStatusFor(app)} title="helm status"><BarChart3 size={14} className="me-1" /> Status</Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setHelmHistoryFor(app)} title="helm history (revisions for rollback)"><History size={14} className="me-1" /> History</Button>
                    {/* Per-service redeploy. The dropdown is populated
                        from the chart's values file (services:) read from
                        the target server — so it always reflects what's
                        actually in this stage's release. */}
                    {(() => {
                      const cache = helmSvcCache[app._id];
                      const busy = helmSvcBusyId === app._id;
                      const services = cache?.services || [];
                      const hasLoaded = !!cache;
                      const showDropdown = hasLoaded && services.length > 0;
                      return (
                        <div className="d-flex gap-1 align-items-center">
                          {showDropdown ? (
                            <Form.Select
                              size="sm"
                              style={{ width: 180 }}
                              value={pickedRestartSvc[app._id] || services[0]}
                              onChange={(e) => setPickedRestartSvc((m) => ({ ...m, [app._id]: e.target.value }))}
                              title={`From ${cache.values_file}`}
                            >
                              {services.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Button
                              size="sm" variant="outline-secondary"
                              disabled={busy}
                              onClick={() => loadHelmServices(app, { force: true })}
                              title={
                                cache?.error
                                  ? `Couldn't read values file: ${cache.error}`
                                  : cache?.note || "Read services from values file"
                              }
                            >
                              {busy
                                ? <Spinner size="sm" animation="border" />
                                : (cache?.error
                                  ? <><AlertTriangle size={14} className="me-1" /> retry services</>
                                  : <><Download size={14} className="me-1" /> load services</>)}
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline-warning"
                            disabled={restartSvcBusyId === app._id || !showDropdown || !(pickedRestartSvc[app._id] || services[0])}
                            onClick={() => helmRestartOne(app)}
                            title="Roll only this deployment — does not run helm"
                          >
                            {restartSvcBusyId === app._id ? <Spinner size="sm" animation="border" /> : <><RotateCw size={14} className="me-1" /> Restart</>}
                          </Button>
                          {showDropdown && (
                            <Button
                              size="sm" variant="link" className="p-0"
                              onClick={() => loadHelmServices(app, { force: true })}
                              title="Re-read values file (e.g. after a git pull)"
                              style={{ textDecoration: "none" }}
                            >
                              ↻
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="success" disabled={deployingId === app._id} onClick={() => gitDeploy(app)}>
                      {deployingId === app._id ? <Spinner size="sm" animation="border" /> : <><Play size={14} className="me-1" /> Deploy</>}
                    </Button>
                    <Button size="sm" variant="outline-warning" disabled={stoppingId === app._id} onClick={() => stopApp(app, false)} title="docker stop the app's container">
                      {stoppingId === app._id ? <Spinner size="sm" animation="border" /> : <><Square size={14} fill="currentColor" className="me-1" /> Stop</>}
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={() => setWizardApp(app)}><Wand2 size={14} className="me-1" /> Wizard</Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setComposeApp(app)}
                      title="Per-service compose status and controls">Compose</Button>
                  </>
                )}
                {/* Mongo panel — common across all modes. Visible whenever
                    the app has at least one target server; the URI is
                    resolved server-side from the app's env_vars first
                    (MONGODB_URI / MONGO_URL / DATABASE_URL), then the
                    server's stored URI. */}
                {(app.target_server_ids || []).length > 0 && (
                  <Button
                    size="sm" variant="outline-primary"
                    onClick={() => setMongoFor({ app, serverId: (app.target_server_ids || [])[0] })}
                    title="Browse / backup / restore the app's MongoDB"
                  >
                    <Database size={14} className="me-1" /> Mongo
                  </Button>
                )}
                {/* Terminal — common across all modes. Opens the browser
                    terminal already cd'd into the app's directory, so you
                    don't have to look up repo_dir and retype it. Hidden for
                    tar_upload (no source dir) and for apps with no target
                    server. One button for a single server, a picker when the
                    app deploys to several — landing on the wrong host is
                    worth one extra click to avoid. */}
                {(() => {
                  const dir = deployDirFor(app);
                  const appServers = serversForApp(app);
                  if (!dir || appServers.length === 0) return null;
                  if (appServers.length === 1) {
                    return (
                      <Button
                        size="sm" variant="outline-secondary"
                        onClick={() => openAppTerminal(app, appServers[0])}
                        title={`Open a terminal in ${dir} on ${appServers[0].label}`}
                      >
                        <SquareTerminal size={14} className="me-1" /> Terminal
                      </Button>
                    );
                  }
                  return (
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="outline-secondary" title={`Open a terminal in ${dir}`}>
                        <SquareTerminal size={14} className="me-1" /> Terminal
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Header>
                          <code className="small">{dir}</code>
                        </Dropdown.Header>
                        {appServers.map((s) => (
                          <Dropdown.Item key={s._id} onClick={() => openAppTerminal(app, s)}>
                            {s.label}
                            <small className="text-muted ms-2">{s.username}@{s.host}</small>
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  );
                })()}
                <Button size="sm" variant="light" onClick={() => setEditing(app)}><Pencil size={14} className="me-1" /> Edit</Button>
                <Button size="sm" variant="outline-danger" onClick={() => removeApp(app)} title="Remove the application from the project" aria-label="Remove application"><X size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AppEditorModal
          projectId={projectId}
          app={editing}
          servers={servers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {composeApp && (
        <ComposeStatusModal
          show={!!composeApp}
          onHide={() => setComposeApp(null)}
          projectId={projectId}
          serverId={String((composeApp.target_server_ids || [])[0] || (servers[0]?._id ?? ""))}
          initialRepoDir={composeApp.repo_dir || ""}
        />
      )}

      {wizardApp && (
        <DeployWizard
          show={!!wizardApp}
          onHide={() => setWizardApp(null)}
          projectId={projectId}
          appId={wizardApp._id}
          servers={servers}
          initial={wizardApp}
          onDeployed={load}
        />
      )}

      {uploadFor && (
        <UploadBuildModal
          projectId={projectId}
          app={uploadFor}
          onClose={() => setUploadFor(null)}
          onUploaded={() => { setUploadFor(null); load(); onActivity && onActivity(); }}
        />
      )}

      {versionsFor && (
        <StaticVersionsModal
          projectId={projectId}
          app={versionsFor}
          onClose={() => setVersionsFor(null)}
          onRolledBack={() => onActivity && onActivity()}
        />
      )}

      {htmlUploadFor && (
        <HtmlDeployModal
          projectId={projectId}
          app={htmlUploadFor}
          onClose={() => setHtmlUploadFor(null)}
          onDeployed={(res) => {
            setHtmlUploadFor(null);
            setResult({
              ok: !!res?.status, name: htmlUploadFor.name,
              results: res?.data?.results || [], message: res?.message,
            });
            load();
            onActivity && onActivity();
          }}
        />
      )}

      {htmlVersionsFor && (
        <HtmlVersionsModal
          projectId={projectId}
          app={htmlVersionsFor}
          onClose={() => setHtmlVersionsFor(null)}
          onReverted={() => { load(); onActivity && onActivity(); }}
        />
      )}

      {pm2StatusFor && (
        <Pm2StatusModal
          projectId={projectId}
          app={pm2StatusFor}
          environment={environment}
          onClose={() => setPm2StatusFor(null)}
        />
      )}

      {pm2LogsFor && (
        <Pm2LogsModal
          projectId={projectId}
          app={pm2LogsFor}
          environment={environment}
          onClose={() => setPm2LogsFor(null)}
        />
      )}

      {helmStatusFor && (
        <HelmStatusModal
          projectId={projectId}
          app={helmStatusFor}
          environment={environment}
          onClose={() => setHelmStatusFor(null)}
        />
      )}

      {helmHistoryFor && (
        <HelmHistoryModal
          projectId={projectId}
          app={helmHistoryFor}
          environment={environment}
          servers={servers}
          onClose={() => setHelmHistoryFor(null)}
          onRolledBack={() => { setHelmHistoryFor(null); load(); onActivity && onActivity(); }}
        />
      )}

      {mongoFor && (
        <MongoPanel
          show={!!mongoFor}
          onHide={() => setMongoFor(null)}
          project={{ _id: projectId, project_name: "" }}
          server={servers.find((s) => String(s._id) === String(mongoFor.serverId)) || { _id: mongoFor.serverId, label: "" }}
          app={mongoFor.app}
          environment={environment}
        />
      )}

      {branchPickerFor && (
        <BranchPickerModal
          projectId={projectId}
          app={branchPickerFor}
          onClose={() => setBranchPickerFor(null)}
          onSwitched={() => { setBranchPickerFor(null); load(); }}
        />
      )}
    </div>
  );
}

const RESTART = ["no", "on-failure", "always", "unless-stopped"];

/**
 * Standalone "Save image" dropdown on docker_build_push app cards.
 *
 * Each item is `<registry>/<context-name>:<tag>` — where `<tag>` is
 * either "latest" (the chart-friendly default) or the strategy
 * configured on the app (git-sha / timestamp). The pushed-image refs
 * from Build & Push always include a `:latest` mirror, so picking the
 * latest version is the predictable choice.
 *
 * For multi-context apps the menu lists every context's :latest;
 * single-context apps still get the dropdown for the .tar/.tar.gz
 * choice, just with one image row.
 */
function BuildPushSaveButton({ app, onPick }) {
  const contexts = (app.build_contexts || []);
  if (!contexts.length) return null;
  return (
    <Dropdown as="span">
      <Dropdown.Toggle as={Button} size="sm" variant="outline-primary" title="Download a previously-built image as a tar archive">
        <Download size={14} className="me-1" /> Save image
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {contexts.map((c) => {
          const tag = "latest";
          const ref = `${app.registry_url}/${c.name}:${tag}`;
          return (
            <React.Fragment key={c.name}>
              <Dropdown.Header className="small text-muted">{c.name}:{tag}</Dropdown.Header>
              <Dropdown.Item onClick={() => onPick(ref, false)}>
                <Download size={12} className="me-2" /> Download .tar
              </Dropdown.Item>
              <Dropdown.Item onClick={() => onPick(ref, true)}>
                <Download size={12} className="me-2" /> Download .tar.gz
              </Dropdown.Item>
            </React.Fragment>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function AppEditorModal({ projectId, app, servers, onClose, onSaved }) {
  const dispatch = useDispatch();
  const [f, setF] = useState({
    name: app.name || "",
    port: app.port || "",
    container_port: app.container_port || "",
    container_name: app.container_name || "",
    mode: app.mode || "git_build",
    image_ref: app.image_ref || "",
    git_url: app.git_url || "",
    git_branch: app.git_branch || "main",
    git_username: app.git_username || "",
    git_password: "",
    repo_dir: app.repo_dir || "",
    static_root: app.static_root || "",
    // html_container keeps 5 by default (the live container plus 4 to revert
    // to); every other mode keeps the historic 3.
    keep_versions: app.keep_versions || (app.mode === "html_container" ? 5 : 3),
    domain: app.domain || "",
    html_root: app.html_root || "",
    html_source: app.html_source || "upload",
    base_image: app.base_image || "nginx:alpine",
    port_source: app.port_source || "auto",
    image_strategy: app.image_strategy || "auto",
    dockerfile_path: app.dockerfile_path || "Dockerfile",
    health_path: app.health_path ?? "/",
    health_timeout_s: app.health_timeout_s || 30,
    node_script: app.node_script || "app.js",
    node_install_cmd: app.node_install_cmd || "npm install --omit=dev",
    node_build_cmd: app.node_build_cmd || "",
    pm2_instances: app.pm2_instances || 1,
    node_version_spec: app.node_version_spec || "",
    run_as_root: !!app.run_as_root,
    stage: app.stage || "development",
    // per_env retained for backward compat with any apps already wired
    // with overrides — the editor no longer exposes it but the value
    // round-trips intact so we don't silently drop existing config.
    per_env: app.per_env || { development: {}, staging: {}, production: {} },
    // Docker build & push (mode=docker_build_push)
    registry_url: app.registry_url || "",
    registry_username: app.registry_username || "",
    registry_password: "",
    has_registry_password: app.has_registry_password,
    build_contexts: (app.build_contexts || []).map((c) => ({ name: c.name, dir: c.dir || "", dockerfile: c.dockerfile || "Dockerfile" })),
    image_tag_strategy: app.image_tag_strategy || "git-sha",
    // Helm chart (mode=helm_chart)
    helm_chart_dir: app.helm_chart_dir || "",
    helm_release_name: app.helm_release_name || "",
    helm_values_file: app.helm_values_file || "",
    helm_set_overrides: app.helm_set_overrides || [],
    helm_namespace: app.helm_namespace || "",
    restart_policy: app.restart_policy || "unless-stopped",
    extra_args: app.extra_args || "",
    port_mappings: (app.port_mappings || []).join("\n"),
    env_vars: app.env_vars || [],
    build_args: app.build_args || [],
    target_server_ids: (app.target_server_ids || []).map(String),
    has_git_password: app.has_git_password,
    mongo_uri: "",
    has_mongo_uri: app.has_mongo_uri,
    mem_limit: app.mem_limit || "",
    mem_reservation: app.mem_reservation || "",
    cpu_limit: app.cpu_limit || "",
  });
  const [saving, setSaving] = useState(false);
  const [inspectFor, setInspectFor] = useState(null); // { path, serverId }
  const [detecting, setDetecting] = useState(false);
  const [detectMsg, setDetectMsg] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Show what auto-detection would pick, without waiting for a deploy. Probes
  // the newest release on the first target server; falls back to repo_dir /
  // html_root when nothing has been deployed yet.
  const detectPortNow = async () => {
    setDetecting(true);
    setDetectMsg(null);
    const res = await dispatch(detectHtmlPort(projectId, app._id, f.target_server_ids[0]));
    setDetecting(false);
    if (res?.status && res.data?.port) {
      set("port", res.data.port);
      setDetectMsg(`Found ${res.data.port} in ${res.data.probed_path}`);
    } else {
      setDetectMsg(res?.message || "No port found — set it manually");
    }
  };

  // Merge-or-overwrite helper used by the Inspect modal. Each call patches
  // a subset of fields; existing values are kept unless the apply explicitly
  // overrides them. env_vars are merged by key (incoming wins on conflict).
  const applyInspected = (patch) => {
    setF((s) => {
      const next = { ...s };
      for (const [k, v] of Object.entries(patch)) {
        if (k === "env_vars") {
          const byKey = new Map();
          for (const e of s.env_vars || []) if (e?.key) byKey.set(e.key, e);
          for (const e of v || []) if (e?.key) byKey.set(e.key, e);
          next.env_vars = Array.from(byKey.values());
        } else {
          next[k] = v;
        }
      }
      return next;
    });
  };

  const toggleServer = (id) =>
    setF((s) => ({
      ...s,
      target_server_ids: s.target_server_ids.includes(id)
        ? s.target_server_ids.filter((x) => x !== id)
        : [...s.target_server_ids, id],
    }));

  const save = async () => {
    setSaving(true);
    const payload = {
      name: f.name.trim(),
      port: f.port ? Number(f.port) : null,
      container_port: f.container_port ? Number(f.container_port) : null,
      container_name: f.container_name.trim(),
      mode: f.mode,
      image_ref: f.image_ref.trim(),
      git_url: f.git_url.trim(),
      git_branch: f.git_branch.trim(),
      git_username: f.git_username.trim(),
      repo_dir: f.repo_dir.trim(),
      static_root: f.static_root.trim(),
      keep_versions: Math.max(1, Math.min(20, Number(f.keep_versions) || (f.mode === "html_container" ? 5 : 3))),
      mem_limit: (f.mem_limit || "").trim(),
      mem_reservation: (f.mem_reservation || "").trim(),
      cpu_limit: (f.cpu_limit || "").trim(),
      domain: (f.domain || "").trim(),
      html_root: (f.html_root || "").trim(),
      html_source: f.html_source === "git" ? "git" : "upload",
      base_image: (f.base_image || "").trim() || "nginx:alpine",
      port_source: f.port_source === "manual" ? "manual" : "auto",
      image_strategy: ["auto", "dockerfile", "base_image"].includes(f.image_strategy) ? f.image_strategy : "auto",
      dockerfile_path: (f.dockerfile_path || "").trim() || "Dockerfile",
      health_path: (f.health_path ?? "").trim(),
      health_timeout_s: Math.max(1, Math.min(600, Number(f.health_timeout_s) || 30)),
      node_script: f.node_script.trim(),
      node_install_cmd: f.node_install_cmd.trim(),
      node_build_cmd: f.node_build_cmd.trim(),
      pm2_instances: Math.max(1, Math.min(32, Number(f.pm2_instances) || 1)),
      node_version_spec: f.node_version_spec || "",
      run_as_root: !!f.run_as_root,
      stage: VALID_STAGES.has(f.stage) ? f.stage : "development",
      registry_url: (f.registry_url || "").trim(),
      registry_username: (f.registry_username || "").trim(),
      build_contexts: (f.build_contexts || [])
        .map((c) => ({ name: (c.name || "").trim(), dir: (c.dir || "").trim(), dockerfile: (c.dockerfile || "Dockerfile").trim() }))
        .filter((c) => c.name),
      image_tag_strategy: f.image_tag_strategy || "git-sha",
      helm_chart_dir: (f.helm_chart_dir || "").trim(),
      helm_release_name: (f.helm_release_name || "").trim(),
      helm_values_file: (f.helm_values_file || "").trim(),
      helm_set_overrides: (f.helm_set_overrides || []).filter((s) => s && s.trim()),
      helm_namespace: (f.helm_namespace || "").trim(),
      // Strip env_vars entries with no key — same hygiene we apply to base.
      per_env: Object.fromEntries(
        ["development", "staging", "production"].map((env) => {
          const o = f.per_env?.[env] || {};
          return [env, {
            ...o,
            env_vars: (o.env_vars || []).filter((e) => e?.key),
          }];
        })
      ),
      restart_policy: f.restart_policy,
      extra_args: f.extra_args,
      port_mappings: f.port_mappings.split("\n").map((x) => x.trim()).filter(Boolean),
      env_vars: f.env_vars.filter((e) => e.key),
      build_args: (f.build_args || []).filter((e) => e.key),
      target_server_ids: f.target_server_ids,
    };
    if (f.git_password) payload.git_password = f.git_password;
    if (f.registry_password) payload.registry_password = f.registry_password;
    // Empty means "leave the stored secret alone"; "__clear__" removes it.
    if (f.mongo_uri) payload.mongo_uri = f.mongo_uri;
    const res = await dispatch(updateDeployApp(projectId, app._id, payload));
    setSaving(false);
    if (res?.status) onSaved();
    else alert(res?.message || "Save failed");
  };

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-flex align-items-center gap-2">
          Edit — {app.name}
          {(() => {
            const m = envMeta(f.stage || "development");
            return (
              <span
                className="badge"
                style={{ background: m.bg, color: m.color, fontWeight: 600, fontSize: 11 }}
                title="App's stage — set at creation. Use 'Move to stage…' below to change it."
              >
                {m.label}
              </span>
            );
          })()}
        </h5>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          {/*
            Mode drives which fields are relevant:
              - Docker modes (git_build, tar_upload) → ports, container_name,
                port mappings, restart policy, extra docker run args
              - pm2_node → container_name (as PM2 process name) only,
                rendered inside the pm2 block below — no docker-run fields
              - static → only static_root + keep_versions, no env vars
          */}
          {(() => null)()}
          <div className="col-md-6">
            <Form.Label>Name *</Form.Label>
            <Form.Control value={f.name} onChange={(e) => set("name", e.target.value)} />
            {/*
              Stage is intentionally NOT an in-form field — it's shown as a
              badge in the modal header and managed via the "Move to stage…"
              link below. Treating it like a regular field made users
              accidentally re-stage apps because the segmented control sat
              next to Name and looked editable. Moving stage is rare.
            */}
            <small className="text-muted d-block mt-1">
              Stage: <strong>{envMeta(f.stage || "development").label}</strong> ·{" "}
              <Button
                type="button"
                variant="link"
                size="sm"
                className="p-0 align-baseline"
                style={{ fontSize: 12 }}
                onClick={() => {
                  const next = window.prompt(
                    `Move "${f.name}" to which stage? Type one of: development / staging / production.\n\nThe app will disappear from this tab and appear under the chosen tab after Save.`,
                    f.stage || "development",
                  );
                  if (!next) return;
                  const v = next.trim().toLowerCase();
                  if (!VALID_STAGES.has(v)) { alert("Invalid stage."); return; }
                  if (v === f.stage) return;
                  set("stage", v);
                }}
              >
                Move to stage…
              </Button>
            </small>
          </div>
          <div className="col-md-6">
            <Form.Label>Source mode</Form.Label>
            <Form.Select
              value={f.mode}
              onChange={(e) => {
                const mode = e.target.value;
                // html_container keeps the live container plus 4 to revert to.
                // The schema-wide default is 3, so switching INTO this mode
                // bumps it to 5 — but only from that generic default, so a
                // deliberate choice is never silently overwritten.
                setF((st) => ({
                  ...st,
                  mode,
                  keep_versions:
                    mode === "html_container" && Number(st.keep_versions) === 3
                      ? 5
                      : st.keep_versions,
                }));
              }}
            >
              <option value="git_build">Git build (clone → docker build)</option>
              <option value="tar_upload">Tar upload (docker load)</option>
              <option value="static">Static build (upload React/Vue/HTML, with rollback)</option>
              <option value="pm2_node">PM2 Node app (git → npm install → pm2)</option>
              <option value="docker_build_push">Docker build &amp; push (multi-service → registry)</option>
              <option value="helm_chart">Helm chart (helm upgrade --install)</option>
              <option value="html_container">HTML site in a container (per-release containers, revert)</option>
            </Form.Select>
          </div>
          {(f.mode === "git_build" || f.mode === "tar_upload") && (
            <>
              <div className="col-md-3">
                <Form.Label>Port</Form.Label>
                <Form.Control type="number" value={f.port} onChange={(e) => set("port", e.target.value)} placeholder="8080" />
              </div>
              <div className="col-md-3">
                <Form.Label>Container port</Form.Label>
                <Form.Control type="number" value={f.container_port} onChange={(e) => set("container_port", e.target.value)} placeholder="= port" />
              </div>
              <div className="col-md-6">
                <Form.Label>Container name *</Form.Label>
                <Form.Control value={f.container_name} onChange={(e) => set("container_name", e.target.value)} placeholder="myapp-backend" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
            </>
          )}

          {f.mode === "pm2_node" ? (
            <>
              <div className="col-md-8">
                <Form.Label>Git URL</Form.Label>
                <Form.Control value={f.git_url} onChange={(e) => set("git_url", e.target.value)} placeholder="https://gitlab.com/acme/api.git" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
              <div className="col-md-4">
                <Form.Label>Branch</Form.Label>
                <Form.Control value={f.git_branch} onChange={(e) => set("git_branch", e.target.value)} />
              </div>
              <div className="col-md-4">
                <Form.Label>Git username</Form.Label>
                <Form.Control value={f.git_username} onChange={(e) => set("git_username", e.target.value)} placeholder="optional" />
              </div>
              <div className="col-md-4">
                <Form.Label>Token / password</Form.Label>
                <Form.Control type="password" value={f.git_password} onChange={(e) => set("git_password", e.target.value)} placeholder={f.has_git_password ? "•••• (saved)" : "optional"} />
              </div>
              <div className="col-md-4">
                <Form.Label>
                  Repo dir on server{" "}
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 ms-1"
                    style={{ fontSize: 12 }}
                    disabled={!f.repo_dir.trim() || !f.target_server_ids.length}
                    onClick={() => setInspectFor({ path: f.repo_dir.trim(), serverId: f.target_server_ids[0] })}
                    title="Read .env / ecosystem.config.js / package.json / git remote from this path on the first target server"
                  >
                    <FolderOpen size={12} className="me-1" /> Inspect
                  </Button>
                </Form.Label>
                <Form.Control value={f.repo_dir} onChange={(e) => set("repo_dir", e.target.value)} placeholder="/opt/apps/api" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
              <div className="col-md-6">
                <Form.Label>
                  Entry script{" "}
                  <small className="text-muted">
                    file, <code>npm:start</code>, <code>npm run X</code>, or <code>ecosystem.config.js [--env X]</code>
                  </small>
                </Form.Label>
                <Form.Control
                  value={f.node_script}
                  onChange={(e) => set("node_script", e.target.value)}
                  placeholder="app.js  ·  npm:start  ·  ecosystem.config.js --env development"
                  style={{ fontFamily: "ui-monospace, monospace" }}
                />
                <small className="text-muted">
                  When using an ecosystem file, the file's <code>name</code> +{" "}
                  <code>instances</code> win — the Container name + PM2 instances
                  fields are ignored for start, but Container name still drives
                  status/logs/restart actions (so set it to the ecosystem app's
                  name).
                </small>
              </div>
              <div className="col-md-3">
                <Form.Label>PM2 instances</Form.Label>
                <Form.Control type="number" min={1} max={32} value={f.pm2_instances} onChange={(e) => set("pm2_instances", e.target.value)} />
                <small className="text-muted">&gt;1 enables cluster mode</small>
              </div>
              <div className="col-md-3">
                <Form.Label>Container name <small className="text-muted">(used as PM2 process name)</small></Form.Label>
                <Form.Control value={f.container_name} onChange={(e) => set("container_name", e.target.value)} placeholder="myapp-api" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
              <div className="col-md-6">
                <Form.Label>Install command</Form.Label>
                <Form.Control value={f.node_install_cmd} onChange={(e) => set("node_install_cmd", e.target.value)} placeholder="npm install --omit=dev" style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
              </div>
              <div className="col-md-6">
                <Form.Label>Build command <small className="text-muted">(optional)</small></Form.Label>
                <Form.Control value={f.node_build_cmd} onChange={(e) => set("node_build_cmd", e.target.value)} placeholder="npm run build" style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
              </div>
              <div className="col-12">
                <Form.Check
                  type="checkbox"
                  id="run-as-root"
                  checked={!!f.run_as_root}
                  onChange={(e) => set("run_as_root", e.target.checked)}
                  label={
                    <span>
                      <strong>Run every step as root</strong>{" "}
                      <small className="text-muted">
                        — git clone, npm install, build, pm2 start/status/logs/restart all run via{" "}
                        <code>sudo -S</code> using the server's stored sudo password. Required when{" "}
                        <code>repo_dir</code> is under a root-only path like <code>/root</code>, or
                        when the app should be managed by <code>pm2-as-root</code>.
                      </small>
                    </span>
                  }
                />
              </div>
              <div className="col-12">
                <Form.Label>Node runtime</Form.Label>
                <NodeVersionPicker
                  projectId={projectId}
                  appId={app._id}
                  servers={servers}
                  targetServerIds={f.target_server_ids}
                  value={f.node_version_spec}
                  onChange={(v) => set("node_version_spec", v)}
                />
              </div>
            </>
          ) : f.mode === "static" ? (
            <>
              <div className="col-md-8">
                <Form.Label>Static root on server</Form.Label>
                <Form.Control value={f.static_root} onChange={(e) => set("static_root", e.target.value)}
                  placeholder="/var/www/myapp" style={{ fontFamily: "ui-monospace, monospace" }} />
                <small className="text-muted">
                  Releases land in <code>{(f.static_root || "/var/www/myapp")}/releases/&lt;ts&gt;/</code>;
                  the <code>current</code> symlink switches between them. Point your nginx vhost root at <code>{(f.static_root || "/var/www/myapp")}/current</code>.
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Keep versions</Form.Label>
                <Form.Control type="number" min={1} max={20} value={f.keep_versions} onChange={(e) => set("keep_versions", e.target.value)} />
              </div>
            </>
          ) : f.mode === "html_container" ? (
            <>
              <div className="col-md-6">
                <Form.Label>Domain</Form.Label>
                <Form.Control value={f.domain} onChange={(e) => set("domain", e.target.value)}
                  placeholder="shop.example.com" style={{ fontFamily: "ui-monospace, monospace" }} />
                <small className="text-muted">
                  Reference only — the portal does <strong>not</strong> write nginx config. Point the
                  DNS A record at the server, then use the <code>nginx</code> button on the card to copy
                  a ready-made proxy block for the Subdomains page.
                </small>
              </div>
              <div className="col-md-6">
                <Form.Label>Release root on server</Form.Label>
                <Form.Control value={f.html_root} onChange={(e) => set("html_root", e.target.value)}
                  placeholder="/var/www/site" style={{ fontFamily: "ui-monospace, monospace" }} />
                <small className="text-muted">
                  Each deploy lands in <code>{(f.html_root || "/var/www/site")}/releases/&lt;ts&gt;/</code>.
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Default source</Form.Label>
                <Form.Select value={f.html_source} onChange={(e) => set("html_source", e.target.value)}>
                  <option value="upload">Upload a .zip / .tar.gz</option>
                  <option value="git">Clone from git on the server</option>
                </Form.Select>
                <small className="text-muted">Either can be picked per deploy.</small>
              </div>
              <div className="col-md-4">
                <Form.Label>Port</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Select
                    value={f.port_source}
                    onChange={(e) => set("port_source", e.target.value)}
                    style={{ maxWidth: 110 }}
                  >
                    <option value="auto">Auto</option>
                    <option value="manual">Manual</option>
                  </Form.Select>
                  <Form.Control
                    type="number" min={1} max={65535}
                    value={f.port}
                    disabled={f.port_source === "auto"}
                    onChange={(e) => set("port", e.target.value)}
                    placeholder="8085"
                  />
                  <Button
                    size="sm" variant="outline-secondary"
                    disabled={detecting || !f.target_server_ids.length}
                    title={f.target_server_ids.length
                      ? "Probe the latest release's .env / ecosystem.config.js"
                      : "Pick a target server first"}
                    onClick={detectPortNow}
                  >
                    {detecting ? <Spinner size="sm" animation="border" /> : "Detect"}
                  </Button>
                </div>
                <small className="text-muted">
                  {detectMsg || (f.port_source === "auto"
                    ? "Read from the release's .env or ecosystem.config.js on every deploy."
                    : "Used as-is.")}
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Keep versions</Form.Label>
                <Form.Control type="number" min={1} max={20} value={f.keep_versions}
                  onChange={(e) => set("keep_versions", e.target.value)} />
                <small className="text-muted">
                  Containers retained — the live one plus {Math.max(0, (Number(f.keep_versions) || 5) - 1)} to revert to.
                </small>
              </div>
              <div className="col-md-6">
                <Form.Label>Container name</Form.Label>
                <Form.Control value={f.container_name} onChange={(e) => set("container_name", e.target.value)}
                  placeholder="mysite" style={{ fontFamily: "ui-monospace, monospace" }} />
                <small className="text-muted">
                  Each release runs as <code>{(f.container_name || "mysite")}-&lt;ts&gt;</code>.
                </small>
              </div>
              <div className="col-md-6">
                <Form.Label>Container port</Form.Label>
                <Form.Control
                  type="number" min={1} max={65535}
                  value={f.container_port}
                  disabled={f.image_strategy === "base_image"}
                  onChange={(e) => set("container_port", e.target.value)}
                  placeholder={f.image_strategy === "base_image" ? "80 (nginx)" : "= published port"}
                />
                <small className="text-muted">
                  What the app listens on <em>inside</em> the container — published as
                  {" "}<code>{f.port || "<port>"}:{f.image_strategy === "base_image" ? 80 : (f.container_port || f.port || "…")}</code>.
                  {f.image_strategy === "base_image"
                    ? " Fixed at 80 when serving with a stock web server."
                    : " Set this when your Dockerfile EXPOSEs something other than the published port."}
                </small>
              </div>

              <div className="col-md-4">
                <Form.Label>Image</Form.Label>
                <Form.Select value={f.image_strategy} onChange={(e) => set("image_strategy", e.target.value)}>
                  <option value="auto">Auto — Dockerfile if present, else base image</option>
                  <option value="dockerfile">Always build the Dockerfile</option>
                  <option value="base_image">Always use the base image</option>
                </Form.Select>
                <small className="text-muted">
                  {f.image_strategy === "dockerfile"
                    ? "A missing Dockerfile fails the deploy rather than silently serving static files."
                    : f.image_strategy === "base_image"
                      ? "Any Dockerfile in the source is ignored."
                      : "Builds the Dockerfile when the release has one."}
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Dockerfile path</Form.Label>
                <Form.Control
                  value={f.dockerfile_path}
                  disabled={f.image_strategy === "base_image"}
                  onChange={(e) => set("dockerfile_path", e.target.value)}
                  placeholder="Dockerfile"
                  style={{ fontFamily: "ui-monospace, monospace" }}
                />
                <small className="text-muted">
                  Relative to the release root, e.g. <code>docker/Dockerfile.prod</code>.
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Base image</Form.Label>
                <Form.Control
                  value={f.base_image}
                  disabled={f.image_strategy === "dockerfile"}
                  onChange={(e) => set("base_image", e.target.value)}
                  placeholder="nginx:alpine" style={{ fontFamily: "ui-monospace, monospace" }}
                />
                <small className="text-muted">
                  Serves the release from <code>/usr/share/nginx/html</code> via a read-only bind-mount.
                </small>
              </div>
              <div className="col-md-8">
                <Form.Label>Health check path</Form.Label>
                <Form.Control
                  value={f.health_path}
                  onChange={(e) => set("health_path", e.target.value)}
                  placeholder="/  (blank disables the check)"
                  style={{ fontFamily: "ui-monospace, monospace" }}
                />
                <small className="text-muted">
                  The new release is started on a spare port and polled here
                  <strong> before</strong> the running one is stopped. If it never
                  answers, the deploy fails and the current container keeps serving —
                  without this a broken image replaces a working site. Leave blank to
                  skip the check and go straight to stop-then-start.
                </small>
              </div>
              <div className="col-md-4">
                <Form.Label>Health timeout (s)</Form.Label>
                <Form.Control
                  type="number" min={1} max={600}
                  value={f.health_timeout_s}
                  disabled={!String(f.health_path || "").trim()}
                  onChange={(e) => set("health_timeout_s", e.target.value)}
                />
                <small className="text-muted">
                  Give a slow starter enough room — a timeout fails the deploy.
                </small>
              </div>
              {f.html_source === "git" && (
                <>
                  <div className="col-md-8">
                    <Form.Label>Git URL</Form.Label>
                    <Form.Control value={f.git_url} onChange={(e) => set("git_url", e.target.value)}
                      placeholder="https://gitlab.com/acme/site.git" style={{ fontFamily: "ui-monospace, monospace" }} />
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Branch</Form.Label>
                    <Form.Control value={f.git_branch} onChange={(e) => set("git_branch", e.target.value)} placeholder="main" />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Git username</Form.Label>
                    <Form.Control value={f.git_username} onChange={(e) => set("git_username", e.target.value)}
                      placeholder="optional — required for a private repo" />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Token / password</Form.Label>
                    <Form.Control
                      type="password" value={f.git_password}
                      onChange={(e) => set("git_password", e.target.value)}
                      placeholder={f.has_git_password ? "•••• (saved)" : "optional — required for a private repo"}
                    />
                    <small className="text-muted">
                      Encrypted at rest. Injected into the clone URL on the server and
                      redacted from the deploy log. Leave both blank for a public repo
                      or an SSH-key URL (<code>git@host:path</code>), which authenticates
                      with the server's own key instead.
                    </small>
                  </div>
                </>
              )}
            </>
          ) : f.mode === "git_build" ? (
            <>
              <div className="col-md-8">
                <Form.Label>Git URL</Form.Label>
                <Form.Control value={f.git_url} onChange={(e) => set("git_url", e.target.value)} placeholder="https://gitlab.com/acme/api.git" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
              <div className="col-md-4">
                <Form.Label>Branch</Form.Label>
                <Form.Control value={f.git_branch} onChange={(e) => set("git_branch", e.target.value)} />
              </div>
              <div className="col-md-4">
                <Form.Label>Git username</Form.Label>
                <Form.Control value={f.git_username} onChange={(e) => set("git_username", e.target.value)} placeholder="optional" />
              </div>
              <div className="col-md-4">
                <Form.Label>Token / password</Form.Label>
                <Form.Control type="password" value={f.git_password} onChange={(e) => set("git_password", e.target.value)} placeholder={f.has_git_password ? "•••• (saved)" : "optional"} />
              </div>
              <div className="col-md-4">
                <Form.Label>Repo dir on server</Form.Label>
                <Form.Control value={f.repo_dir} onChange={(e) => set("repo_dir", e.target.value)} placeholder="/opt/apps/api" style={{ fontFamily: "ui-monospace, monospace" }} />
              </div>
              <div className="col-md-4">
                <Form.Label>Dockerfile path</Form.Label>
                <Form.Control
                  value={f.dockerfile_path}
                  onChange={(e) => set("dockerfile_path", e.target.value)}
                  placeholder="Dockerfile"
                  style={{ fontFamily: "ui-monospace, monospace" }}
                />
                <small className="text-muted">
                  Relative to the repo root, e.g. <code>docker/Dockerfile.app</code>.
                  The build context stays the repo root.
                </small>
              </div>
              {/* A monorepo Dockerfile often builds one of several apps from a
                  shared recipe (`ARG APP_NAME`), and the arg is then the only
                  thing distinguishing two otherwise identical builds. */}
              <div className="col-12">
                <Form.Label>
                  Build args <small className="text-muted">(passed to docker build --build-arg)</small>
                </Form.Label>
                {(f.build_args || []).map((e, i) => (
                  <div key={i} className="d-flex gap-2 mb-2">
                    <Form.Control
                      size="sm" placeholder="APP_NAME" value={e.key}
                      style={{ fontFamily: "ui-monospace, monospace" }}
                      onChange={(ev) => set("build_args", f.build_args.map((x, idx) => idx === i ? { ...x, key: ev.target.value } : x))}
                    />
                    <Form.Control
                      size="sm" placeholder="user_service" value={e.value}
                      style={{ fontFamily: "ui-monospace, monospace" }}
                      onChange={(ev) => set("build_args", f.build_args.map((x, idx) => idx === i ? { ...x, value: ev.target.value } : x))}
                    />
                    <Button size="sm" variant="outline-danger" aria-label="Remove"
                      onClick={() => set("build_args", f.build_args.filter((_, idx) => idx !== i))}><X size={14} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="light"
                  onClick={() => set("build_args", [...(f.build_args || []), { key: "", value: "" }])}>
                  + Add build arg
                </Button>
                <div className="small text-muted mt-1">
                  Build time only — these are baked into the image and visible in
                  <code className="ms-1">docker history</code>, so secrets belong in
                  environment variables instead.
                </div>
              </div>
            </>
          ) : f.mode === "docker_build_push" ? (
            <BuildPushFields f={f} set={set} />
          ) : f.mode === "helm_chart" ? (
            <HelmChartFields f={f} set={set} />
          ) : (
            <div className="col-md-6">
              <Form.Label>Image ref</Form.Label>
              <Form.Control value={f.image_ref} onChange={(e) => set("image_ref", e.target.value)} placeholder="myapp:latest" style={{ fontFamily: "ui-monospace, monospace" }} />
            </div>
          )}

          {(f.mode === "git_build" || f.mode === "tar_upload") && (
            <>
              <div className="col-md-6">
                <Form.Label>Extra port mappings <small className="text-muted">(one per line)</small></Form.Label>
                <Form.Control as="textarea" rows={2} value={f.port_mappings} onChange={(e) => set("port_mappings", e.target.value)} placeholder={"443:8443\n127.0.0.1:9000:9000"} style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
              </div>
              <div className="col-md-6">
                <Form.Label>Restart policy</Form.Label>
                <Form.Select value={f.restart_policy} onChange={(e) => set("restart_policy", e.target.value)}>
                  {RESTART.map((r) => <option key={r} value={r}>{r}</option>)}
                </Form.Select>
              </div>
              <div className="col-12">
                <Form.Label>Extra docker run args <small className="text-muted">(advanced, inserted verbatim)</small></Form.Label>
                <Form.Control value={f.extra_args} onChange={(e) => set("extra_args", e.target.value)} placeholder="--memory 512m --user 1000" style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }} />
              </div>
            </>
          )}

          {/* Static deploys are just a tarball + symlink swap — no
              runtime env, so the env editor is hidden for that mode. */}
          {f.mode !== "static" && (
            <div className="col-12">
              <Form.Label>Environment variables</Form.Label>
              {f.env_vars.map((e, i) => (
                <div key={i} className="d-flex gap-2 mb-2">
                  <Form.Control size="sm" placeholder="KEY" value={e.key} onChange={(ev) => set("env_vars", f.env_vars.map((x, idx) => idx === i ? { ...x, key: ev.target.value } : x))} />
                  <Form.Control size="sm" placeholder="value" value={e.value} onChange={(ev) => set("env_vars", f.env_vars.map((x, idx) => idx === i ? { ...x, value: ev.target.value } : x))} />
                  <Button size="sm" variant="outline-danger" aria-label="Remove" onClick={() => set("env_vars", f.env_vars.filter((_, idx) => idx !== i))}><X size={14} /></Button>
                </div>
              ))}
              <div className="d-flex gap-2 align-items-center">
                <Button size="sm" variant="light" onClick={() => set("env_vars", [...f.env_vars, { key: "", value: "" }])}>+ Add variable</Button>
                <Button size="sm" variant="link" className="p-0" onClick={() => set("env_vars", [])} disabled={!f.env_vars.length}>
                  Clear all
                </Button>
              </div>
              <EnvBulkEditor value={f.env_vars} onChange={(v) => set("env_vars", v)} />
            </div>
          )}

          {/* Resource limits. Every docker-backed mode runs through
              buildRunCmd(), so these apply uniformly. Hidden for static,
              which has no container of its own. */}
          {f.mode !== "static" && (
            <div className="col-12">
              <Form.Label>
                Resource limits <small className="text-muted">(optional, per container)</small>
              </Form.Label>
              <div className="row g-2">
                <div className="col-md-4">
                  <Form.Control
                    value={f.mem_limit}
                    onChange={(e) => set("mem_limit", e.target.value)}
                    placeholder="Memory limit — e.g. 512m"
                    style={{ fontFamily: "ui-monospace, monospace" }}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Control
                    value={f.mem_reservation}
                    onChange={(e) => set("mem_reservation", e.target.value)}
                    placeholder="Memory reserve — e.g. 256m"
                    style={{ fontFamily: "ui-monospace, monospace" }}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Control
                    value={f.cpu_limit}
                    onChange={(e) => set("cpu_limit", e.target.value)}
                    placeholder="CPUs — e.g. 0.5"
                    style={{ fontFamily: "ui-monospace, monospace" }}
                  />
                </div>
              </div>
              <small className="text-muted">
                Maps to <code>--memory</code> / <code>--memory-reservation</code> /
                {" "}<code>--cpus</code>. Leave blank for unlimited — but note an
                unlimited container that leaks can exhaust the host's RAM and take
                every other app on that server down with it; a limit means the kernel
                OOM-kills just this one. Set the limit from observed usage
                (<code>docker stats</code> on the Docker page), not a guess: too low
                turns a working app into a restart loop.
              </small>
            </div>
          )}

          {/* Per-app Mongo URI. Sits next to the env editor because it's the
              explicit override for the same thing the env vars are scanned
              for — and it applies to every mode. */}
          <div className="col-12">
            <Form.Label>
              MongoDB URI <small className="text-muted">(for this application's Backup / Restore)</small>
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="password"
                value={f.mongo_uri}
                onChange={(e) => set("mongo_uri", e.target.value)}
                placeholder={f.has_mongo_uri
                  ? "•••• (saved — type to replace)"
                  : "mongodb://user:pass@127.0.0.1:27017/dbname"}
                style={{ fontFamily: "ui-monospace, monospace" }}
              />
              {f.has_mongo_uri && (
                <Button
                  size="sm" variant="outline-danger"
                  title="Remove the stored URI for this app"
                  onClick={() => {
                    if (window.confirm("Clear this application's stored Mongo URI?")) {
                      set("mongo_uri", "__clear__");
                      set("has_mongo_uri", false);
                    }
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
            <small className="text-muted">
              Encrypted at rest and never sent back to the browser. Used first, ahead of
              {" "}<code>MONGODB_URI</code>/<code>MONGO_URI</code>/<code>MONGO_URL</code>/<code>DATABASE_URL</code>
              {" "}in the env vars above, then the server's own URI. Set it when the app's env
              value isn't reachable from the server itself — e.g. the app uses
              {" "}<code>mongodb://mongo:27017/db</code> inside its Docker network, which
              {" "}<code>mongodump</code> over SSH can't resolve.
            </small>
          </div>

          <div className="col-12">
            <Form.Label>Target servers <small className="text-muted">(base — used by an env that doesn't override)</small></Form.Label>
            <div className="apps-servers">
              {servers.length === 0 && <small className="text-muted">No servers on this project.</small>}
              {servers.map((s) => (
                <label key={s._id} className="apps-server-check">
                  <input type="checkbox" checked={f.target_server_ids.includes(String(s._id))} onChange={() => toggleServer(String(s._id))} />
                  {s.label} <code className="small">{s.username}@{s.host}</code>
                </label>
              ))}
            </div>
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving || !f.name.trim()}>
          {saving ? <Spinner size="sm" animation="border" /> : "Save"}
        </Button>
      </Modal.Footer>
      {inspectFor && (
        <InspectPathModal
          projectId={projectId}
          appId={app._id}
          serverId={inspectFor.serverId}
          path={inspectFor.path}
          servers={servers}
          targetServerIds={f.target_server_ids}
          onClose={() => setInspectFor(null)}
          onApply={(patch) => { applyInspected(patch); setInspectFor(null); }}
        />
      )}
    </Modal>
  );
}

/**
 * Upload a built static site (React/Vue/HTML) — a .zip or .tar.gz.
 * The backend extracts it under `<static_root>/releases/<ts>/`, swaps the
 * `current` symlink, and prunes to `keep_versions`. A common gotcha is
 * zipping the `build/` folder itself instead of its contents — the backend
 * auto-flattens when the archive has exactly one top-level directory, so
 * either form works.
 */
function UploadBuildModal({ projectId, app, onClose, onUploaded }) {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const valid = !!file && /\.(zip|tar\.gz|tgz|tar)$/i.test(file.name);

  const upload = async () => {
    if (!file || !valid) return;
    setUploading(true);
    setResult(null);
    setProgress(0);
    const res = await dispatch(uploadStaticBuild(projectId, app._id, file, setProgress));
    setUploading(false);
    setResult(res);
    if (res?.status) onUploaded && onUploaded();
  };

  return (
    <Modal show onHide={() => !uploading && onClose()} size="lg" centered backdrop="static">
      <Modal.Header closeButton={!uploading}>
        <h5 className="m-0">Upload build — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        {!result ? (
          <>
            <div className="mb-3 small text-muted">
              Pick a <code>.zip</code> or <code>.tar.gz</code> of the build output (e.g. the contents of <code>build/</code> or <code>dist/</code>).
              The build will be extracted at{" "}
              <code>{app.static_root}/releases/&lt;ts&gt;/</code> and the{" "}
              <code>current</code> symlink will switch to it. The last{" "}
              <strong>{app.keep_versions || 3}</strong> releases are kept for rollback.
            </div>
            <Form.Group>
              <Form.Label>Build archive *</Form.Label>
              <Form.Control
                type="file"
                accept=".zip,.tar.gz,.tgz,.tar,application/zip,application/gzip,application/x-tar"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <small className={`d-block mt-1 ${valid ? "text-muted" : "text-danger"}`}>
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB
                  {!valid && " — must be .zip / .tar.gz / .tgz / .tar"}
                </small>
              )}
            </Form.Group>
            {uploading && (
              <div className="mt-3">
                <div className="progress" style={{ height: 14 }}>
                  <div className="progress-bar" style={{ width: `${progress}%` }}>{progress}%</div>
                </div>
                <small className="text-muted">
                  {progress < 100 ? "Uploading…" : "Extracting + swapping symlink on the server…"}
                </small>
              </div>
            )}
          </>
        ) : (
          <div className={`alert ${result.status ? "alert-success" : "alert-danger"}`}>
            <strong className="d-inline-flex align-items-center gap-2">
              {result.status ? <Check size={14} /> : <X size={14} />} {result.message}
            </strong>
            {result.data?.release && (
              <div className="small mt-1">Release: <code>{result.data.release}</code></div>
            )}
            {(result.data?.results || []).map((r) => (
              <pre key={r.server_id} className="wiz-log mt-2">
                {`# ${r.server_label || r.server_id} — ${r.status}\n${r.log || r.message || ""}`}
              </pre>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result ? (
          <>
            <Button variant="light" onClick={onClose} disabled={uploading}>Cancel</Button>
            <Button variant="primary" onClick={upload} disabled={!valid || uploading}>
              {uploading ? <Spinner size="sm" animation="border" /> : <><Upload size={14} className="me-1" /> Upload</>}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>Done</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

/**
 * List the releases kept on the target server. The `current` row is
 * highlighted; any other row can be activated via "Rollback to this", which
 * just re-points the `current` symlink (no re-extract).
 */
/**
 * Upload a .zip/.tar.gz and run it as a new container for an html_container
 * app. The git-sourced path needs no modal — it fires straight from the card.
 */
function HtmlDeployModal({ projectId, app, onClose, onDeployed }) {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!file) { setErr("Pick a .zip or .tar.gz of the site first"); return; }
    setBusy(true);
    setErr(null);
    setPct(0);
    const res = await dispatch(
      deployHtmlContainer(projectId, app._id, { source: "upload", file }, setPct),
    );
    setBusy(false);
    if (res?.status) onDeployed && onDeployed(res);
    else setErr(res?.message || "Deploy failed");
  };

  const keep = Math.max(1, Number(app.keep_versions) || 5);

  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}>
        <h5 className="m-0">Deploy {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Site archive (.zip / .tar.gz)</Form.Label>
          <Form.Control
            type="file"
            accept=".zip,.tar.gz,.tgz,.tar"
            disabled={busy}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(null); }}
          />
          <small className="text-muted">
            An archive that wraps everything in a single top-level folder (a
            <code> build/</code>, say) is flattened automatically.
          </small>
        </Form.Group>
        <div className="text-muted small">
          Runs as a new container on port <code>{app.port || "auto-detected"}</code>. The
          current container is stopped but kept — {keep} versions are retained in
          total, so you can revert to any of the previous {keep - 1}.
        </div>
        {busy && (
          <div className="mt-3">
            <div className="progress" style={{ height: 6 }}>
              <div className="progress-bar" style={{ width: `${pct}%` }} />
            </div>
            <small className="text-muted">
              {pct < 100 ? `Uploading ${pct}%…` : "Extracting and starting the container…"}
            </small>
          </div>
        )}
        {err && <div className="alert alert-danger mt-3 mb-0">{err}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="success" onClick={submit} disabled={busy || !file}>
          {busy ? <Spinner size="sm" animation="border" /> : <><Upload size={14} className="me-1" /> Deploy</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * The retained containers for an html_container app, newest first, with a
 * Revert on each stopped one. Mirrors StaticVersionsModal.
 */
function HtmlVersionsModal({ projectId, app, onClose, onReverted }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const targetServers = app.target_server_ids || [];
  const [serverId, setServerId] = useState(targetServers[0] ? String(targetServers[0]) : "");

  const load = async () => {
    if (!serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(listHtmlVersions(projectId, app._id, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Failed to list containers");
  };

  // `load` is recreated every render; depending on it would re-fetch in a
  // loop. Reloading when the chosen server changes is the intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [serverId]);

  const revert = async (release) => {
    if (!window.confirm(
      `Revert to release ${release}?\n\nThe live container is stopped and this one started — ` +
      `about two seconds, no rebuild, and the port doesn't change.`
    )) return;
    setBusy(release);
    const res = await dispatch(revertHtmlContainer(projectId, app._id, release));
    setBusy(null);
    if (res?.status) { load(); onReverted && onReverted(); }
    else alert(res?.message || "Revert failed");
  };

  const when = (ts) => {
    const n = Number(ts);
    return Number.isFinite(n) ? new Date(n).toLocaleString() : ts;
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <h5 className="m-0">Containers — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        {targetServers.length > 1 && (
          <Form.Group className="mb-3">
            <Form.Label>Server</Form.Label>
            <Form.Select size="sm" value={serverId} onChange={(e) => setServerId(e.target.value)}>
              {targetServers.map((id) => (
                <option key={id} value={id}>{String(id)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {data && (
          <div className="text-muted small mb-2">
            <code>{data.container_name}</code> on port <code>{data.port || "—"}</code>
            {data.domain ? <> · <code>{data.domain}</code></> : null}
            {" · keeping "}{data.keep_versions}
          </div>
        )}
        {err && <div className="alert alert-danger">{err}</div>}
        <div className="apps-versions-wrap">
          {loading ? (
            <div className="text-center py-3"><Spinner size="sm" animation="border" /> Loading…</div>
          ) : !data?.versions?.length ? (
            <div className="text-muted">No containers yet — deploy this app first.</div>
          ) : (
            <table className="emp-table mb-0">
              <thead>
                <tr>
                  <th>Release</th>
                  <th>Status</th>
                  <th>Image</th>
                  <th className="text-end" style={{ width: 1 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.versions.map((v) => (
                  <tr key={v.name}>
                    <td>
                      <code className="small">{v.release}</code>
                      <div className="text-muted small">{when(v.release)}</div>
                    </td>
                    <td>
                      {v.running
                        ? <span className="badge bg-success">live</span>
                        : <span className="badge bg-secondary">stopped</span>}
                      <div className="text-muted small">{v.status}</div>
                    </td>
                    <td><code className="small">{v.image}</code></td>
                    <td className="text-end">
                      {v.running ? (
                        <span className="text-muted small">current</span>
                      ) : v.revertable ? (
                        <Button size="sm" variant="outline-primary" disabled={busy === v.release}
                          onClick={() => revert(v.release)}>
                          {busy === v.release
                            ? <Spinner size="sm" animation="border" />
                            : <><History size={14} className="me-1" /> Revert</>}
                        </Button>
                      ) : (
                        // Bind-mounted release whose directory was pruned —
                        // starting it would serve an empty site.
                        <span className="text-muted small" title="Its release directory is gone, so it would start empty">
                          unavailable
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

function StaticVersionsModal({ projectId, app, onClose, onRolledBack }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  const targetServers = app.target_server_ids || [];
  const [serverId, setServerId] = useState(targetServers[0] ? String(targetServers[0]) : "");

  const load = async () => {
    if (!serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(listStaticVersions(projectId, app._id, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Failed to list versions");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serverId]);

  const rollback = async (release) => {
    if (!window.confirm(`Switch the live release to ${release}? (just re-points the symlink — fast)`)) return;
    setBusy(release);
    const res = await dispatch(rollbackStaticRelease(projectId, app._id, release));
    setBusy(null);
    if (res?.status) { load(); onRolledBack && onRolledBack(); }
    else alert(res?.message || "Rollback failed");
  };

  const fmt = (b) => {
    if (!b) return "—";
    const u = ["B","KB","MB","GB"];
    let i = 0, v = b;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <h5 className="m-0">Versions — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        {targetServers.length > 1 && (
          <Form.Group className="mb-3">
            <Form.Label>Server</Form.Label>
            <Form.Select size="sm" value={serverId} onChange={(e) => setServerId(e.target.value)}>
              {targetServers.map((id) => (
                <option key={id} value={id}>{String(id)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        {data && (
          <div className="text-muted small mb-2">
            Root: <code>{data.static_root}</code> · Current: <code>{data.current || "—"}</code>
          </div>
        )}
        {err && <div className="alert alert-danger">{err}</div>}
        <div className="apps-versions-wrap">
          {loading ? (
            <div className="text-center py-3"><Spinner size="sm" animation="border" /> Loading…</div>
          ) : !data?.releases?.length ? (
            <div className="text-muted">No releases yet — upload a build first.</div>
          ) : (
            <table className="emp-table mb-0">
              <thead>
                <tr>
                  <th>Release</th>
                  <th>Size</th>
                  <th>Modified</th>
                  <th className="text-end" style={{ width: 1 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.releases.map((r) => (
                  <tr key={r.name}>
                    <td>
                      <code className="small">{r.name}</code>
                      {r.is_current && <span className="badge bg-success ms-2">current</span>}
                    </td>
                    <td className="small">{fmt(r.size)}</td>
                    <td className="small">{r.mtime ? new Date(r.mtime).toLocaleString() : "—"}</td>
                    <td className="text-end">
                      {r.is_current ? (
                        <span className="text-muted small">— active —</span>
                      ) : (
                        <Button size="sm" variant="outline-primary" disabled={busy === r.name} onClick={() => rollback(r.name)}>
                          {busy === r.name ? <Spinner size="sm" animation="border" /> : "↺ Rollback"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={load} disabled={loading}>↻ Refresh</Button>
        <Button variant="primary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * PM2 status — `pm2 jlist` parsed per target server. Shows process state,
 * uptime, CPU, memory, restarts, instance count. The app's own process is
 * highlighted; other PM2-managed processes on the server are listed below.
 */
function Pm2StatusModal({ projectId, app, environment = "development", onClose }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(pm2Status(projectId, app._id, environment));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Failed to fetch pm2 status");
  }, [dispatch, projectId, app._id, environment]);

  useEffect(() => { load(); }, [load]);

  const fmtUp = (ms) => {
    if (!ms || ms < 0) return "—";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ${m % 60}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  };
  const fmtMem = (b) => {
    if (!b) return "—";
    const u = ["B","KB","MB","GB"]; let i = 0, v = b;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 && i ? 1 : 0)} ${u[i]}`;
  };
  const stColor = (s) => ({ online: "success", stopped: "secondary", errored: "danger", launching: "warning", "stopping": "warning" }[s] || "secondary");

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton><h5 className="m-0">PM2 status — {app.name}</h5></Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger">{err}</div>}
        {loading && !data ? (
          <div className="text-center py-4"><Spinner size="sm" animation="border" /> Loading…</div>
        ) : !data?.results?.length ? (
          <div className="text-muted">No target servers.</div>
        ) : (
          data.results.map((srv) => (
            <div key={srv.server_id} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="small">{srv.server_label}</strong>
                {srv.status !== "success" && <span className="badge bg-danger">{srv.status}</span>}
              </div>
              {srv.message && <div className="small text-muted mb-2">{srv.message}</div>}
              {srv.app && (
                <table className="emp-table mb-2">
                  <thead><tr><th>Name</th><th>Status</th><th>Up</th><th>CPU</th><th>Mem</th><th>↻</th><th>Inst.</th></tr></thead>
                  <tbody>
                    <tr>
                      <td><code className="small">{srv.app.name}</code></td>
                      <td><span className={`badge bg-${stColor(srv.app.status)}`}>{srv.app.status || "—"}</span></td>
                      <td className="small">{fmtUp(srv.app.uptime_ms)}</td>
                      <td className="small">{srv.app.cpu != null ? `${srv.app.cpu}%` : "—"}</td>
                      <td className="small">{fmtMem(srv.app.memory)}</td>
                      <td className="small">{srv.app.restarts ?? 0}</td>
                      <td className="small">{srv.app.instances ?? 1}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {!srv.app && srv.status === "success" && (
                <div className="small text-muted">Process not registered with PM2 yet — hit Deploy.</div>
              )}
              {(srv.all || []).filter((p) => !srv.app || p.name !== srv.app.name).length > 0 && (
                <details className="small">
                  <summary className="text-muted">Other PM2 processes on this server ({(srv.all || []).filter((p) => !srv.app || p.name !== srv.app.name).length})</summary>
                  <table className="emp-table mt-1">
                    <tbody>
                      {(srv.all || []).filter((p) => !srv.app || p.name !== srv.app.name).map((p) => (
                        <tr key={p.pm_id}>
                          <td><code className="small">{p.name}</code></td>
                          <td><span className={`badge bg-${stColor(p.status)}`}>{p.status}</span></td>
                          <td className="small">{fmtUp(p.uptime_ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={load} disabled={loading}>↻ Refresh</Button>
        <Button variant="primary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * PM2 logs — tail of `pm2 logs <name> --nostream --raw` across target
 * servers. Lines slider lets you pull more history without re-opening.
 */
function Pm2LogsModal({ projectId, app, environment = "development", onClose }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [lines, setLines] = useState(200);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(pm2Logs(projectId, app._id, lines, environment));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Failed to fetch logs");
  }, [dispatch, projectId, app._id, lines, environment]);

  useEffect(() => { load(); }, [load]);

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0">PM2 logs — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2 align-items-center mb-2">
          <Form.Label className="m-0 small">Lines</Form.Label>
          <Form.Select size="sm" value={lines} onChange={(e) => setLines(Number(e.target.value))} style={{ width: 100 }}>
            {[100, 200, 500, 1000, 2000].map((n) => <option key={n} value={n}>{n}</option>)}
          </Form.Select>
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻ Refresh"}
          </Button>
        </div>
        {err && <div className="alert alert-danger">{err}</div>}
        {loading && !data ? (
          <div className="text-center py-4"><Spinner size="sm" animation="border" /> Loading…</div>
        ) : !data?.results?.length ? (
          <div className="text-muted">No target servers.</div>
        ) : (
          data.results.map((srv) => (
            <div key={srv.server_id} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="small">{srv.server_label}</strong>
                <span className={`badge bg-${srv.status === "success" ? "success" : srv.status === "notice" ? "secondary" : "danger"}`}>{srv.status}</span>
              </div>
              {srv.message && <div className="small text-muted mb-1">{srv.message}</div>}
              {srv.log != null && (
                <pre className="apps-log" style={{ maxHeight: 360, overflowY: "auto" }}>
                  {srv.log || "(empty)"}
                </pre>
              )}
            </div>
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Branch picker — lists remote branches via `git ls-remote` on a target
 * server, lets the user switch the app's active branch. The switch just
 * updates the stored `git_branch`; the next Deploy does the actual
 * fetch + checkout + reset --hard on each target.
 */
function BranchPickerModal({ projectId, app, onClose, onSwitched }) {
  const dispatch = useDispatch();
  const targets = (app.target_server_ids || []).map(String);
  const [serverId, setServerId] = useState(targets[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    if (!serverId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(listGitBranches(projectId, app._id, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else { setData(null); setErr(res?.message || "Failed to list branches"); }
  }, [dispatch, projectId, app._id, serverId]);

  useEffect(() => { load(); }, [load]);

  const pick = async (b) => {
    if (b === (app.git_branch || "main")) return;
    if (!window.confirm(`Switch branch from "${app.git_branch || "main"}" to "${b}"? The next Deploy will use it.`)) return;
    setSwitching(true);
    const res = await dispatch(switchGitBranch(projectId, app._id, b));
    setSwitching(false);
    if (res?.status) onSwitched && onSwitched();
    else alert(res?.message || "Switch failed");
  };

  const list = (data?.branches || []).filter((b) => !filter || b.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Modal show onHide={onClose} size="md" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0">Switch branch — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted small mb-2">
          Current: <code>{app.git_branch || "main"}</code> ·{" "}
          <code className="small">{app.git_url}</code>
        </div>
        {targets.length > 1 && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Probe from server</Form.Label>
            <Form.Select size="sm" value={serverId} onChange={(e) => setServerId(e.target.value)}>
              {targets.map((id) => <option key={id} value={id}>{id}</option>)}
            </Form.Select>
          </Form.Group>
        )}
        <Form.Control
          size="sm"
          placeholder="Filter branches…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-2"
          disabled={loading || !!err}
        />
        {err && <div className="alert alert-warning small">{err}</div>}
        {loading ? (
          <div className="text-center py-3"><Spinner size="sm" animation="border" /> Loading branches…</div>
        ) : !data?.branches?.length ? (
          !err && <div className="text-muted small">No branches found.</div>
        ) : (
          <div className="apps-branch-list">
            {list.map((b) => {
              const isCurrent = b === (app.git_branch || "main");
              return (
                <button
                  key={b}
                  type="button"
                  className={`apps-branch-item${isCurrent ? " on" : ""}`}
                  disabled={switching || isCurrent}
                  onClick={() => pick(b)}
                  title={isCurrent ? "Already active" : `Switch to ${b}`}
                >
                  <code>{b}</code>
                  {isCurrent && <span className="badge bg-success ms-2">current</span>}
                </button>
              );
            })}
            {!list.length && <div className="text-muted small">No matches.</div>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={load} disabled={loading || switching}>↻ Refresh</Button>
        <Button variant="primary" onClick={onClose} disabled={switching}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Node-runtime picker for the pm2_node mode. Probes the chosen target
 * server (or, on demand, a different one) for installed Node versions
 * across all the common managers (nvm/n/fnm/asdf/volta/system) and lets
 * the user pick one. The selected `spec` string ("nvm:v18.17.0" etc.)
 * is what the deploy code uses to prepend PATH to install / build /
 * pm2 start commands. Empty / "default" / "system" = use whatever's on
 * the SSH user's PATH.
 *
 * The picker fetches lazily on mount, so opening the editor on a fresh
 * server triggers one probe. A Re-detect button switches the probe
 * source if you want to confirm a different target has the version.
 */
function NodeVersionPicker({ projectId, appId, servers, targetServerIds, value, onChange }) {
  const dispatch = useDispatch();
  const candidateIds = (targetServerIds || []).map(String);
  const [probeId, setProbeId] = useState(candidateIds[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!probeId || !appId) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(listNodeVersions(projectId, appId, probeId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else { setData(null); setErr(res?.message || "Failed to detect"); }
  }, [dispatch, projectId, appId, probeId]);

  useEffect(() => { load(); }, [load]);

  const versions = data?.versions || [];

  return (
    <div>
      <div className="d-flex gap-2 align-items-center mb-2">
        <Form.Select size="sm" value={value || ""} onChange={(e) => onChange(e.target.value)} style={{ maxWidth: 360 }}>
          <option value="">— default (use $PATH node) —</option>
          {value && !versions.find((v) => v.spec === value) && (
            <option value={value}>{value} (saved — not detected on this server)</option>
          )}
          {versions.map((v) => (
            <option key={`${v.spec}-${v.version}`} value={v.spec}>{v.label}</option>
          ))}
        </Form.Select>
        {candidateIds.length > 1 && (
          <Form.Select size="sm" value={probeId} onChange={(e) => setProbeId(e.target.value)} style={{ maxWidth: 220 }} title="Probe from server">
            {candidateIds.map((id) => {
              const s = servers.find((x) => String(x._id) === id);
              return <option key={id} value={id}>{s ? s.label : id}</option>;
            })}
          </Form.Select>
        )}
        <Button size="sm" variant="light" onClick={load} disabled={loading || !probeId}>
          {loading ? <Spinner size="sm" animation="border" /> : "↻ Detect"}
        </Button>
      </div>
      {!probeId && <small className="text-muted">Pick a target server first to detect installed Node versions.</small>}
      {err && <div className="alert alert-warning small py-1 mb-1">{err}</div>}
      {data && !err && (
        <small className="text-muted">
          {versions.length
            ? `${versions.length} runtime${versions.length === 1 ? "" : "s"} detected on ${data.server_label} — newest first.`
            : `No Node installs found on ${data.server_label}.`}
          {" "}
          {/* Which account was probed is the first thing to check when the
              list looks wrong: nvm installed for root won't be visible to the
              SSH user, and vice versa. The probe follows run_as_root so it
              matches where the deploy will actually look. */}
          Probed as <code>{data.probed_user || "?"}</code>
          {data.probed_home ? <> (HOME <code>{data.probed_home}</code>)</> : null}
          {data.as_root ? " — matching this app's run-as-root setting." : "."}
          {!versions.length && " Install nvm or system node for that account."}
        </small>
      )}
    </div>
  );
}

/**
 * "Inspect" — reads /env, package.json, ecosystem.config.js, and git
 * remote from the chosen folder on the chosen target server, then renders
 * a "what was found" panel with per-section Apply buttons that patch the
 * surrounding AppEditor form. Each section is independently applicable
 * so the admin can take env vars from .env, the script + instances from
 * ecosystem, and the URL from git separately.
 *
 * Idempotent: re-probing the same path overwrites the panel. env_vars
 * are merged by key (incoming wins on conflict) so multiple applies
 * don't dup rows.
 */
function InspectPathModal({ projectId, appId, serverId, path, servers, targetServerIds, onClose, onApply }) {
  const dispatch = useDispatch();
  const [probeServer, setProbeServer] = useState(serverId || targetServerIds?.[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [editFor, setEditFor] = useState(null); // { name } — e.g. ".env"

  const load = useCallback(async () => {
    if (!probeServer || !path) return;
    setLoading(true);
    setErr(null);
    const res = await dispatch(inspectServerPath(projectId, probeServer, path));
    setLoading(false);
    if (res?.status) setData(res.data);
    else { setData(null); setErr(res?.message || "Inspect failed"); }
  }, [dispatch, projectId, probeServer, path]);

  useEffect(() => { load(); }, [load]);

  const applyEnv = () => onApply({ env_vars: data?.env_vars || [] });
  const applyGit = () => onApply({
    git_url: data?.git?.url || "",
    git_branch: data?.git?.branch || "main",
  });
  const applyPackage = () => {
    const p = data?.package;
    if (!p) return;
    const patch = {};
    // Prefer the start script (`npm:start`) when one exists — pm2 then
    // runs `npm start` which respects package.json's intent.
    if (p.scripts?.includes("start")) patch.node_script = "npm:start";
    else if (p.main) patch.node_script = p.main;
    if (p.has_build_script) patch.node_build_cmd = "npm run build";
    if (p.name) patch.container_name = p.name.replace(/[^a-zA-Z0-9_.\-]/g, "-");
    onApply(patch);
  };
  const applyPm2 = (idx) => {
    const a = data?.pm2?.apps?.[idx];
    if (!a) return;
    const patch = {};
    if (a.name) patch.container_name = a.name;
    if (a.cwd) patch.repo_dir = a.cwd;
    if (a.script) {
      const rel = a.cwd && a.script.startsWith(a.cwd)
        ? a.script.slice(a.cwd.length).replace(/^\/+/, "")
        : a.script;
      patch.node_script = rel || a.script;
    }
    if (typeof a.instances === "number" && a.instances > 0) {
      patch.pm2_instances = Math.min(32, a.instances);
    }
    // env block on the pm2 entry → merge into our env_vars[]
    if (a.env && typeof a.env === "object") {
      patch.env_vars = Object.entries(a.env).map(([key, value]) => ({ key, value: String(value) }));
    }
    onApply(patch);
  };

  const pkg = data?.package;
  const pm2 = data?.pm2;
  const git = data?.git;

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2"><FolderOpen size={18} /> Inspect path</h5>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
          <code className="small">{path}</code>
          {targetServerIds?.length > 1 && (
            <Form.Select size="sm" value={probeServer} onChange={(e) => setProbeServer(e.target.value)} style={{ maxWidth: 240 }}>
              {targetServerIds.map((id) => {
                const s = servers.find((x) => String(x._id) === id);
                return <option key={id} value={id}>{s ? s.label : id}</option>;
              })}
            </Form.Select>
          )}
          <Button size="sm" variant="light" onClick={load} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "↻ Re-probe"}
          </Button>
        </div>

        {err && <div className="alert alert-warning small">{err}</div>}

        {loading && !data ? (
          <div className="text-center py-3"><Spinner animation="border" /> Reading files…</div>
        ) : !data ? null : (
          <>
            {/* .env */}
            <div className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="d-inline-flex align-items-center gap-1"><FileIcon size={14} /> .env <span className="badge bg-secondary ms-1">{data.env_vars.length} vars</span></strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" disabled={!appId} onClick={() => setEditFor({ name: ".env", serverId: probeServer })}>
                    <Pencil size={12} className="me-1" /> Edit
                  </Button>
                  <Button size="sm" variant="primary" disabled={!data.env_vars.length} onClick={applyEnv}>
                    Apply env vars
                  </Button>
                </div>
              </div>
              {data.env_vars.length === 0 ? (
                <small className="text-muted">No .env at this path.</small>
              ) : (
                <pre className="small mb-0" style={{ maxHeight: 160, overflowY: "auto", background: "var(--bg-raised)", padding: 8, borderRadius: 4 }}>
                  {data.env_vars.map((e) => `${e.key}=${e.value.length > 60 ? e.value.slice(0, 60) + "…" : e.value}`).join("\n")}
                </pre>
              )}
            </div>

            {/* package.json */}
            <div className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="d-inline-flex align-items-center gap-1"><Package size={14} /> package.json</strong>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="outline-secondary" disabled={!appId || !pkg} onClick={() => setEditFor({ name: "package.json", serverId: probeServer })}>
                    <Pencil size={12} className="me-1" /> Edit
                  </Button>
                  <Button size="sm" variant="primary" disabled={!pkg} onClick={applyPackage}>
                    Apply name + start script
                  </Button>
                </div>
              </div>
              {!pkg ? (
                <small className="text-muted">No package.json at this path.</small>
              ) : (
                <div className="small">
                  <div><code>name:</code> {pkg.name || "—"} · <code>version:</code> {pkg.version || "—"}</div>
                  <div><code>main:</code> {pkg.main || "—"}</div>
                  <div><code>scripts:</code> {pkg.scripts.join(", ") || "—"}</div>
                </div>
              )}
            </div>

            {/* ecosystem.config.js */}
            <div className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="d-inline-flex align-items-center gap-1"><Settings size={14} /> ecosystem.config.js</strong>
                <Button
                  size="sm" variant="outline-secondary"
                  disabled={!appId}
                  onClick={() => setEditFor({ name: "ecosystem.config.js", serverId: probeServer })}
                  title="Edit (also reads ecosystem.config.cjs if present)"
                >
                  <Pencil size={12} className="me-1" /> Edit
                </Button>
              </div>
              {!pm2 ? (
                <small className="text-muted">No ecosystem.config.js at this path.</small>
              ) : pm2.error ? (
                <small className="text-danger">Parse error: {pm2.error}</small>
              ) : !pm2.apps?.length ? (
                <small className="text-muted">No apps[] in ecosystem file.</small>
              ) : (
                <table className="emp-table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th><th>Script</th><th>Instances</th><th style={{ width: 1 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {pm2.apps.map((a, i) => (
                      <tr key={i}>
                        <td><code className="small">{a.name || "—"}</code></td>
                        <td><code className="small">{a.script || "—"}</code></td>
                        <td>{a.instances === 0 ? "max" : a.instances}</td>
                        <td className="text-end">
                          <Button size="sm" variant="primary" onClick={() => applyPm2(i)}>Apply</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* git remote */}
            <div className="mb-3 p-2 border rounded">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="d-inline-flex align-items-center gap-1"><LinkIcon size={14} /> git remote</strong>
                <Button size="sm" variant="primary" disabled={!git?.url} onClick={applyGit}>
                  Apply git URL + branch
                </Button>
              </div>
              {!git ? (
                <small className="text-muted">No .git at this path.</small>
              ) : (
                <div className="small">
                  <div><code>url:</code> {git.url}</div>
                  <div><code>branch:</code> {git.branch || "—"} · <code>sha:</code> {git.sha || "—"}</div>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>Close</Button>
      </Modal.Footer>
      {editFor && appId && (
        <DeployFileEditor
          projectId={projectId}
          appId={appId}
          serverId={editFor.serverId || probeServer}
          path={`${(path || "").replace(/\/+$/, "")}/${editFor.name}`}
          onClose={() => setEditFor(null)}
          onSaved={() => { setEditFor(null); load(); }}
        />
      )}
    </Modal>
  );
}

/**
 * Lightweight server-file editor used from the Inspect modal. Reads
 * the file via the deploy-file endpoint (sudo-aware), shows it in a
 * textarea, and writes it back through the SFTP-to-tmp + `sudo mv`
 * pattern when `run_as_root` is set on the app. Kept simple (textarea,
 * no syntax highlight) so it loads instantly and doesn't drag in a
 * CodeMirror dependency for the deploy page.
 */
function DeployFileEditor({ projectId, appId, serverId, path, onClose, onSaved }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [meta, setMeta] = useState(null); // { size, mtime, mode }
  const [content, setContent] = useState("");
  const [original, setOriginal] = useState("");
  const dirty = content !== original;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const res = await dispatch(readDeployFile(projectId, appId, path, serverId));
    setLoading(false);
    if (res?.status) {
      setContent(res.data.content);
      setOriginal(res.data.content);
      setMeta({ size: res.data.size, mtime: res.data.mtime, mode: res.data.mode });
    } else {
      setErr(res?.message || "Failed to read file");
    }
  }, [dispatch, projectId, appId, path, serverId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setErr(null);
    const res = await dispatch(writeDeployFile(projectId, appId, path, content, serverId));
    setSaving(false);
    if (res?.status) {
      setOriginal(content);
      onSaved && onSaved();
    } else {
      setErr(res?.message || "Save failed");
    }
  };

  return (
    <Modal show onHide={onClose} size="xl" centered scrollable backdrop="static">
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2">
          <Pencil size={16} /> Edit <code className="small">{path}</code>
        </h5>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger small">{err}</div>}
        {meta && (
          <div className="small text-muted mb-1">
            {meta.size} bytes · last modified{" "}
            {meta.mtime ? new Date(meta.mtime).toLocaleString() : "—"}
            {meta.mode ? ` · mode ${meta.mode}` : ""}
            {dirty && <span className="badge bg-warning ms-2">unsaved</span>}
          </div>
        )}
        {loading && !content && !err ? (
          <div className="text-center py-3"><Spinner animation="border" /> Reading…</div>
        ) : (
          <Form.Control
            as="textarea"
            rows={20}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            spellCheck={false}
            disabled={loading || saving}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button
          size="sm"
          variant="link"
          className="p-0"
          onClick={() => setContent(original)}
          disabled={!dirty || saving}
        >
          Reset to disk
        </Button>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={!dirty || saving || loading}>
            {saving ? <Spinner size="sm" animation="border" /> : "Save"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Bulk paste/edit for env vars. Renders a collapsible textarea below the
 * single-row form. The textarea holds the env vars in `.env` format
 * (`KEY=VALUE` per line) and is the canonical view for paste-import-from-
 * .env workflows. Two modes:
 *
 *   ▸ "Merge" — paste a chunk; click Apply; existing keys are overwritten
 *     by incoming, new keys are appended. Useful when bringing in a few
 *     additional vars without losing the current ones.
 *
 *   ▸ "Replace" — Apply replaces the entire env_vars list with what's in
 *     the textarea. Useful when copying an entire .env over.
 *
 * Parsing handles surrounding "/' quotes, comments (# at line start),
 * and blank lines. Multi-line values (heredoc-style) are NOT supported —
 * .env spec doesn't standardize them and pm2 wouldn't read them anyway.
 */
function EnvBulkEditor({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const [mode, setMode] = React.useState("merge"); // 'merge' | 'replace'
  const [err, setErr] = React.useState(null);
  const [okMsg, setOkMsg] = React.useState(null);

  // Pull-from-current — opening the editor pre-fills with the existing
  // env_vars in .env format so the user can edit instead of starting blank.
  // We only do this when text is empty so we don't clobber in-progress edits.
  React.useEffect(() => {
    if (open && !text) {
      const dump = (value || [])
        .filter((e) => e?.key)
        .map((e) => `${e.key}=${e.value ?? ""}`)
        .join("\n");
      setText(dump);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parse = () => {
    const out = [];
    const errs = [];
    const lines = text.split("\n");
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const line = raw.trim();
      if (!line || line.startsWith("#")) continue;
      // Strip optional `export ` prefix that shell-style .env's sometimes use.
      const stripped = line.replace(/^export\s+/i, "");
      const m = stripped.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*)$/);
      if (!m) {
        errs.push(`line ${i + 1}: not KEY=VALUE — "${line.slice(0, 60)}"`);
        continue;
      }
      let v = m[2];
      // Strip surrounding quotes — common in .env files generated by docker
      // or compose. Don't touch internal quotes (those are part of the value).
      if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
        v = v.slice(1, -1);
      }
      out.push({ key: m[1], value: v });
    }
    return { rows: out, errs };
  };

  const apply = () => {
    setErr(null);
    setOkMsg(null);
    const { rows, errs } = parse();
    if (errs.length) {
      setErr(`${errs.length} bad line${errs.length === 1 ? "" : "s"}: ${errs.slice(0, 3).join("; ")}${errs.length > 3 ? "…" : ""}`);
      return;
    }
    if (mode === "replace") {
      onChange(rows);
      setOkMsg(`Replaced — ${rows.length} variable${rows.length === 1 ? "" : "s"}`);
    } else {
      // Merge: incoming overrides existing by key, new keys appended.
      const byKey = new Map();
      for (const e of value || []) if (e?.key) byKey.set(e.key, e);
      let overridden = 0;
      for (const e of rows) {
        if (byKey.has(e.key)) overridden++;
        byKey.set(e.key, e);
      }
      const merged = Array.from(byKey.values());
      onChange(merged);
      setOkMsg(
        `Merged — ${rows.length - overridden} added, ${overridden} updated, ${merged.length} total`,
      );
    }
  };

  if (!open) {
    return (
      <Button
        size="sm"
        variant="link"
        className="p-0 mt-1"
        onClick={() => setOpen(true)}
        style={{ fontSize: 13 }}
      >
        <span className="d-inline-flex align-items-center gap-1"><ClipboardPaste size={13} /> Paste .env / bulk edit</span>
      </Button>
    );
  }

  return (
    <div className="mt-2 p-2" style={{ background: "var(--bg-raised)", border: "1px solid var(--line)", borderRadius: 6 }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <strong className="small d-inline-flex align-items-center gap-1"><ClipboardPaste size={13} /> Bulk env editor</strong>
        <div className="d-flex gap-2 align-items-center">
          <Form.Check
            inline type="radio" id="bulk-merge" name="bulk-mode" label="Merge"
            checked={mode === "merge"} onChange={() => setMode("merge")}
          />
          <Form.Check
            inline type="radio" id="bulk-replace" name="bulk-mode" label="Replace"
            checked={mode === "replace"} onChange={() => setMode("replace")}
          />
          <Button size="sm" variant="link" className="p-0" onClick={() => setOpen(false)}>
            Hide
          </Button>
        </div>
      </div>
      <Form.Control
        as="textarea"
        rows={8}
        value={text}
        onChange={(e) => { setText(e.target.value); setErr(null); setOkMsg(null); }}
        placeholder={`# Paste KEY=VALUE lines (.env shape)\nDATABASE_URL=postgres://...\nNODE_ENV=production\n# comments and blank lines ignored\nexport PORT=8080`}
        style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
      />
      <div className="d-flex justify-content-between align-items-center mt-1">
        <small className="text-muted">
          {mode === "merge"
            ? "Merge: existing keys overwritten by pasted; new keys appended."
            : "Replace: this list becomes the entire env_vars."}
        </small>
        <div className="d-flex gap-2">
          <Button size="sm" variant="light" onClick={() => setText("")}>Clear text</Button>
          <Button size="sm" variant="primary" onClick={apply} disabled={!text.trim()}>
            Apply
          </Button>
        </div>
      </div>
      {err && <div className="alert alert-warning small mt-1 mb-0 py-1">{err}</div>}
      {okMsg && <div className="alert alert-success small mt-1 mb-0 py-1">{okMsg}</div>}
    </div>
  );
}

/**
 * Compact 3-segment env switcher rendered in the page header. Same colour
 * vocabulary as the banner below so the active env is reinforced in two
 * places — easy to spot at a glance even when scrolled past the banner.
 */
function EnvSwitcher({ value, onChange }) {
  return (
    <div className="btn-group" role="group" aria-label="Environment">
      {ENV_OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`btn btn-sm ${active ? "active" : ""}`}
            onClick={() => onChange(opt.value)}
            style={{
              background: active ? opt.color : "#fff",
              color: active ? "#fff" : "#374151",
              borderColor: active ? opt.color : "#d1d5db",
              fontWeight: active ? 600 : 400,
            }}
            title={`Switch to ${opt.label}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Per-environment overrides editor — appended at the bottom of the
 * AppEditorModal body. Each environment tab shows the same five fields
 * (git_branch, target_server_ids, pm2_instances, port, env_vars); any
 * unset value inherits from the base config above. Helps reduce config
 * duplication: shared keys live on base, only the env-specific ones get
 * overridden here.
 *
 * The bulk-paste editor from the base form is reused for the per-env
 * env_vars so multi-line .env paste works the same way.
 */
function PerEnvEditor({ value, onChange, servers }) {
  const [tab, setTab] = React.useState("development");
  const cur = value?.[tab] || {};
  const updateField = (field, fieldValue) => {
    onChange({
      ...(value || {}),
      [tab]: { ...cur, [field]: fieldValue },
    });
  };

  const toggleServer = (id) => {
    const list = cur.target_server_ids || [];
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    updateField("target_server_ids", next);
  };

  return (
    <div className="border rounded p-2" style={{ background: "var(--bg-raised)" }}>
      <ul className="nav nav-pills mb-2">
        {ENV_OPTIONS.map((opt) => (
          <li className="nav-item" key={opt.value}>
            <button
              type="button"
              className={`nav-link py-1 ${tab === opt.value ? "active" : ""}`}
              onClick={() => setTab(opt.value)}
              style={tab === opt.value ? { background: opt.color, color: "#fff" } : { color: opt.color }}
            >
              {opt.label}
            </button>
          </li>
        ))}
      </ul>
      <small className="text-muted d-block mb-2">
        Unset values inherit from the base config above.{" "}
        <code>env_vars</code> merges by key (env wins); other fields replace base when set.
      </small>
      <div className="row g-2">
        <div className="col-md-6">
          <Form.Label className="small mb-1">Git branch</Form.Label>
          <Form.Control
            size="sm"
            value={cur.git_branch || ""}
            onChange={(e) => updateField("git_branch", e.target.value)}
            placeholder="inherit"
            style={{ fontFamily: "ui-monospace, monospace" }}
          />
        </div>
        <div className="col-md-3">
          <Form.Label className="small mb-1">PM2 instances</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={1}
            max={32}
            value={cur.pm2_instances ?? ""}
            onChange={(e) => updateField("pm2_instances", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="inherit"
          />
        </div>
        <div className="col-md-3">
          <Form.Label className="small mb-1">Port</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={1}
            max={65535}
            value={cur.port ?? ""}
            onChange={(e) => updateField("port", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="inherit"
          />
        </div>
        <div className="col-12">
          <Form.Label className="small mb-1">Target servers</Form.Label>
          <div className="apps-servers" style={{ maxHeight: 120 }}>
            {servers.length === 0 && <small className="text-muted">No servers on this project.</small>}
            {servers.map((s) => (
              <label key={s._id} className="apps-server-check">
                <input
                  type="checkbox"
                  checked={(cur.target_server_ids || []).map(String).includes(String(s._id))}
                  onChange={() => toggleServer(String(s._id))}
                />
                {s.label} <code className="small">{s.username}@{s.host}</code>
              </label>
            ))}
          </div>
          <small className="text-muted">
            Empty = use the base "Target servers" selection. Any pick here REPLACES base for this env.
          </small>
        </div>
        <div className="col-12">
          <Form.Label className="small mb-1">
            Environment variables{" "}
            <small className="text-muted">(merges with base by key — env wins)</small>
          </Form.Label>
          {(cur.env_vars || []).map((e, i) => (
            <div key={i} className="d-flex gap-1 mb-1">
              <Form.Control
                size="sm"
                placeholder="KEY"
                value={e.key}
                onChange={(ev) => updateField("env_vars", (cur.env_vars || []).map((x, idx) => idx === i ? { ...x, key: ev.target.value } : x))}
              />
              <Form.Control
                size="sm"
                placeholder="value"
                value={e.value}
                onChange={(ev) => updateField("env_vars", (cur.env_vars || []).map((x, idx) => idx === i ? { ...x, value: ev.target.value } : x))}
              />
              <Button
                size="sm" variant="outline-danger"
                aria-label="Remove env var"
                onClick={() => updateField("env_vars", (cur.env_vars || []).filter((_, idx) => idx !== i))}
              >
                <X size={14} />
              </Button>
            </div>
          ))}
          <div className="d-flex gap-2 align-items-center">
            <Button size="sm" variant="light" onClick={() => updateField("env_vars", [...(cur.env_vars || []), { key: "", value: "" }])}>
              + Add variable
            </Button>
            <Button
              size="sm" variant="link" className="p-0"
              disabled={!(cur.env_vars || []).length}
              onClick={() => updateField("env_vars", [])}
            >
              Clear all
            </Button>
          </div>
          <EnvBulkEditor value={cur.env_vars || []} onChange={(v) => updateField("env_vars", v)} />
        </div>
      </div>
    </div>
  );
}

/* ───── Docker build & push form ───── */

function BuildPushFields({ f, set }) {
  const ctxs = f.build_contexts || [];
  const updateCtx = (i, patch) =>
    set("build_contexts", ctxs.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  return (
    <>
      <div className="col-md-8">
        <Form.Label>Git URL</Form.Label>
        <Form.Control value={f.git_url} onChange={(e) => set("git_url", e.target.value)} placeholder="https://gitlab.com/acme/microservices.git" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>Branch</Form.Label>
        <Form.Control value={f.git_branch} onChange={(e) => set("git_branch", e.target.value)} />
      </div>
      <div className="col-md-4">
        <Form.Label>Git username</Form.Label>
        <Form.Control value={f.git_username} onChange={(e) => set("git_username", e.target.value)} placeholder="optional" />
      </div>
      <div className="col-md-4">
        <Form.Label>Git token / password</Form.Label>
        <Form.Control type="password" value={f.git_password} onChange={(e) => set("git_password", e.target.value)} placeholder={f.has_git_password ? "•••• (saved)" : "optional"} />
      </div>
      <div className="col-md-4">
        <Form.Label>Repo dir on server</Form.Label>
        <Form.Control value={f.repo_dir} onChange={(e) => set("repo_dir", e.target.value)} placeholder="/opt/charts/myapp" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-12">
        <Form.Label>
          Registry URL{" "}
          <small className="text-muted">
            (fully qualified prefix — image refs become{" "}
            <code>&lt;registry&gt;/&lt;context-name&gt;:&lt;tag&gt;</code>)
          </small>
        </Form.Label>
        <Form.Control
          value={f.registry_url}
          onChange={(e) => set("registry_url", e.target.value)}
          placeholder="e.g. registry.gitlab.com/acme/tsd-microservices"
          style={{ fontFamily: "ui-monospace, monospace" }}
        />
      </div>
      <div className="col-md-4">
        <Form.Label>Registry username</Form.Label>
        <Form.Control value={f.registry_username} onChange={(e) => set("registry_username", e.target.value)} placeholder="optional — for `docker login`" />
      </div>
      <div className="col-md-4">
        <Form.Label>Registry token / password</Form.Label>
        <Form.Control type="password" value={f.registry_password} onChange={(e) => set("registry_password", e.target.value)} placeholder={f.has_registry_password ? "•••• (saved)" : "optional"} />
      </div>
      <div className="col-md-4">
        <Form.Label>Image tag strategy</Form.Label>
        <Form.Select value={f.image_tag_strategy} onChange={(e) => set("image_tag_strategy", e.target.value)}>
          <option value="git-sha">git short SHA (+ also tag :latest)</option>
          <option value="timestamp">unix ms (+ also tag :latest)</option>
          <option value="fixed-latest">always :latest</option>
        </Form.Select>
      </div>
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="m-0">
            Build contexts{" "}
            <small className="text-muted">
              (one entry per microservice — empty = build repo root once as the app's container_name)
            </small>
          </Form.Label>
          <Button
            size="sm"
            variant="link"
            className="p-0"
            onClick={() => set("build_contexts", [...ctxs, { name: "", dir: "", dockerfile: "Dockerfile" }])}
          >
            + Add context
          </Button>
        </div>
        {ctxs.length === 0 && (
          <small className="text-muted">No contexts — will build <code>{f.repo_dir || "<repo_dir>"}/Dockerfile</code> as one image.</small>
        )}
        {ctxs.map((c, i) => (
          <div key={i} className="d-flex gap-1 mb-1">
            <Form.Control
              size="sm"
              placeholder="image name (e.g. auth-service)"
              value={c.name}
              onChange={(e) => updateCtx(i, { name: e.target.value })}
              style={{ maxWidth: 200, fontFamily: "ui-monospace, monospace" }}
            />
            <Form.Control
              size="sm"
              placeholder="subdir (default = name)"
              value={c.dir}
              onChange={(e) => updateCtx(i, { dir: e.target.value })}
              style={{ maxWidth: 220, fontFamily: "ui-monospace, monospace" }}
            />
            <Form.Control
              size="sm"
              placeholder="Dockerfile"
              value={c.dockerfile}
              onChange={(e) => updateCtx(i, { dockerfile: e.target.value })}
              style={{ maxWidth: 180, fontFamily: "ui-monospace, monospace" }}
            />
            <Button
              size="sm"
              variant="outline-danger"
              aria-label="Remove build context"
              onClick={() => set("build_contexts", ctxs.filter((_, idx) => idx !== i))}
            >
              <X size={14} />
            </Button>
          </div>
        ))}
        {ctxs.length > 0 && (
          <small className="text-muted">
            Each becomes <code>{f.registry_url || "<registry>"}/&lt;name&gt;:&lt;tag&gt;</code>
          </small>
        )}
      </div>
    </>
  );
}

/* ───── Helm chart form ───── */

function HelmChartFields({ f, set }) {
  const sets = f.helm_set_overrides || [];
  return (
    <>
      <div className="col-md-8">
        <Form.Label>Git URL <small className="text-muted">(repo containing the chart)</small></Form.Label>
        <Form.Control value={f.git_url} onChange={(e) => set("git_url", e.target.value)} placeholder="https://gitlab.com/acme/microservices.git" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>Branch</Form.Label>
        <Form.Control value={f.git_branch} onChange={(e) => set("git_branch", e.target.value)} />
      </div>
      <div className="col-md-4">
        <Form.Label>Git username</Form.Label>
        <Form.Control value={f.git_username} onChange={(e) => set("git_username", e.target.value)} placeholder="optional" />
      </div>
      <div className="col-md-4">
        <Form.Label>Git token / password</Form.Label>
        <Form.Control type="password" value={f.git_password} onChange={(e) => set("git_password", e.target.value)} placeholder={f.has_git_password ? "•••• (saved)" : "optional"} />
      </div>
      <div className="col-md-4">
        <Form.Label>Repo dir on server</Form.Label>
        <Form.Control value={f.repo_dir} onChange={(e) => set("repo_dir", e.target.value)} placeholder="/opt/charts/myapp" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>Chart dir <small className="text-muted">(relative to repo_dir)</small></Form.Label>
        <Form.Control value={f.helm_chart_dir} onChange={(e) => set("helm_chart_dir", e.target.value)} placeholder="helm-chart" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>Release name <small className="text-muted">(defaults to container_name)</small></Form.Label>
        <Form.Control value={f.helm_release_name} onChange={(e) => set("helm_release_name", e.target.value)} placeholder="tsd" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>
          Values file{" "}
          <small className="text-muted">
            (blank = auto <code>values-&lt;stage&gt;.yaml</code> → <code>values.yaml</code>)
          </small>
        </Form.Label>
        <Form.Control value={f.helm_values_file} onChange={(e) => set("helm_values_file", e.target.value)} placeholder="values-production.yaml" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>
          Namespace override{" "}
          <small className="text-muted">(blank = server's per-stage map)</small>
        </Form.Label>
        <Form.Control value={f.helm_namespace} onChange={(e) => set("helm_namespace", e.target.value)} placeholder="tsd-prod" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-md-4">
        <Form.Label>Container name <small className="text-muted">(used as default release name)</small></Form.Label>
        <Form.Control value={f.container_name} onChange={(e) => set("container_name", e.target.value)} placeholder="tsd" style={{ fontFamily: "ui-monospace, monospace" }} />
      </div>
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="m-0">
            <code>--set</code> overrides{" "}
            <small className="text-muted">(one KEY=VALUE per line)</small>
          </Form.Label>
          <Button
            size="sm" variant="link" className="p-0"
            onClick={() => set("helm_set_overrides", [...sets, ""])}
          >
            + Add override
          </Button>
        </div>
        {sets.map((s, i) => (
          <div key={i} className="d-flex gap-1 mb-1">
            <Form.Control
              size="sm"
              placeholder="common.image.repository=registry.gitlab.com/acme/svcs"
              value={s}
              onChange={(e) => set("helm_set_overrides", sets.map((x, idx) => idx === i ? e.target.value : x))}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            />
            <Button
              size="sm" variant="outline-danger"
              aria-label="Remove override"
              onClick={() => set("helm_set_overrides", sets.filter((_, idx) => idx !== i))}
            >
              <X size={14} />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}

/* ───── Helm status modal ───── */

function HelmStatusModal({ projectId, app, environment, onClose }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    const res = await dispatch(helmStatus(projectId, app._id, environment));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Failed to read status");
  }, [dispatch, projectId, app._id, environment]);

  useEffect(() => { load(); }, [load]);

  const sevForStatus = (s) =>
    s === "deployed" ? "success" : s === "failed" ? "danger" : s === "pending-install" || s === "pending-upgrade" ? "warning" : "secondary";

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2"><Anchor size={16} /> Helm status — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger small">{err}</div>}
        {loading && !data ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : !data?.results?.length ? (
          <div className="text-muted">No target servers.</div>
        ) : data.results.map((r) => (
          <div key={r.server_id} className="mb-3 border rounded p-2">
            <div className="d-flex justify-content-between align-items-center">
              <strong className="small">{r.server_label}</strong>
              <span className={`badge bg-${r.status === "success" ? "success" : r.status === "notice" ? "secondary" : "danger"}`}>
                {r.status}
              </span>
            </div>
            {r.message && <div className="small text-muted">{r.message}</div>}
            {r.info && (
              <table className="emp-table mb-0 mt-2">
                <tbody>
                  <tr><td>Release</td><td><code className="small">{r.info.name}</code></td></tr>
                  <tr><td>Revision</td><td><code className="small">{r.info.version}</code></td></tr>
                  <tr><td>Status</td><td><span className={`badge bg-${sevForStatus(r.info.status)}`}>{r.info.status}</span></td></tr>
                  <tr><td>Namespace</td><td><code className="small">{r.namespace || "—"}</code></td></tr>
                  <tr><td>Last deployed</td><td className="small">{r.info.last_deployed ? new Date(r.info.last_deployed).toLocaleString() : "—"}</td></tr>
                  {r.info.description && <tr><td>Description</td><td className="small">{r.info.description}</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={load} disabled={loading}>↻ Refresh</Button>
        <Button variant="primary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ───── Helm history + rollback modal ───── */

function HelmHistoryModal({ projectId, app, environment, servers, onClose, onRolledBack }) {
  const dispatch = useDispatch();
  const targets = (app.target_server_ids || []).map(String);
  const [probeServer, setProbeServer] = useState(targets[0] || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);

  const load = useCallback(async () => {
    if (!probeServer) return;
    setLoading(true); setErr(null);
    const res = await dispatch(helmHistory(projectId, app._id, probeServer, environment));
    setLoading(false);
    if (res?.status) setData(res.data);
    else { setData(null); setErr(res?.message || "Failed to read history"); }
  }, [dispatch, projectId, app._id, probeServer, environment]);

  useEffect(() => { load(); }, [load]);

  const doRollback = async (revision) => {
    if (!window.confirm(`Roll back ${data?.release || app.name} to revision ${revision}?`)) return;
    if (environment === "production" && !window.confirm(`Final check: PRODUCTION rollback. Continue?`)) return;
    setRollingBack(true);
    const res = await dispatch(helmRollback(projectId, app._id, revision, environment));
    setRollingBack(false);
    if (res?.status) onRolledBack && onRolledBack();
    else alert(res?.message || "Rollback failed");
  };

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2"><History size={16} /> Helm history — {app.name}</h5>
      </Modal.Header>
      <Modal.Body>
        {targets.length > 1 && (
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Server</Form.Label>
            <Form.Select size="sm" value={probeServer} onChange={(e) => setProbeServer(e.target.value)}>
              {targets.map((id) => {
                const s = servers.find((x) => String(x._id) === id);
                return <option key={id} value={id}>{s ? s.label : id}</option>;
              })}
            </Form.Select>
          </Form.Group>
        )}
        {data && (
          <div className="small text-muted mb-2">
            Release <code>{data.release}</code> · ns <code>{data.namespace || "—"}</code>
          </div>
        )}
        {err && <div className="alert alert-warning small">{err}</div>}
        {loading && !data ? (
          <div className="text-center py-3"><Spinner animation="border" /></div>
        ) : !data?.revisions?.length ? (
          <div className="text-muted">No revisions yet on this server.</div>
        ) : (
          <table className="emp-table mb-0">
            <thead>
              <tr>
                <th>Rev</th><th>When</th><th>Status</th><th>Chart</th><th>Description</th>
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {data.revisions.slice().reverse().map((r) => (
                <tr key={r.revision}>
                  <td><strong>{r.revision}</strong></td>
                  <td className="small">{r.updated ? new Date(r.updated).toLocaleString() : "—"}</td>
                  <td><span className={`badge bg-${r.status === "deployed" ? "success" : r.status === "superseded" ? "secondary" : "warning"}`}>{r.status}</span></td>
                  <td className="small"><code>{r.chart || "—"}</code></td>
                  <td className="small">{r.description || "—"}</td>
                  <td className="text-end">
                    {r.status !== "deployed" && (
                      <Button
                        size="sm" variant="outline-warning"
                        disabled={rollingBack}
                        onClick={() => doRollback(r.revision)}
                      >
                        {rollingBack ? <Spinner size="sm" animation="border" /> : "↺ Rollback"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={load} disabled={loading}>↻ Refresh</Button>
        <Button variant="primary" onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
